import { Component, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CadViewerComponent } from "@components/cadViewer/cadViewer";
import { BusinessSector } from "@enums/businessSector.enum";
import { Currency } from "@enums/currency.enum";
import { Incoterms } from "@enums/incoterms.enum";
import { MaterialQuoting } from "@enums/materialQuoting.enum";
import { MaterialType } from "@enums/materialType.enum";
import { PackagingTerms } from "@enums/packagingTerms.enum";
import { PurchaseTypeEnum } from "@enums/purchaseType.enum";
import { ToolingFixtureDescription } from "@enums/toolingFixtureDescription.enum";
import { ToolOwnershipEnum } from "@enums/toolOwnership.enum";
import { environment } from "@environments/environment";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { densityForMaterialType } from "@helpers/materialTypeDensity";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { BoughtOutPartsLineData } from "@interfaces/boughtOutPartsLineData.interface";
import { ClientContact } from "@interfaces/clientContact.interface";
import { CreateSalesOrderFromPart } from "@interfaces/createSalesOrderFromPart.interface";
import { GenerateAndDownloadQuotationAnalysisFormPDFDto } from "@interfaces/dto/generateAndDownloadQuotationAnalysisFormPDFDto";
import { Machine } from "@interfaces/models/machine.interface";
import { Client } from "@interfaces/models/client.interface";
import { OutsourcedSpecification } from "@interfaces/models/outsourcedSpecification.interface";
import { Packaging } from "@interfaces/models/packaging.interface";
import { PaymentTerm } from "@interfaces/models/paymentTerm.interface";
import { ProductionTool } from "@interfaces/models/productionTool.interface";
import { ProductionToolPart } from "@interfaces/models/productionToolPart.interface";
import { ToolingType } from "@interfaces/models/toolingType.interface";
import { Supplier } from "@interfaces/models/supplier.interface";
import { OutsourcedProcessLineData } from "@interfaces/outsourcedProcessLineData.interface";
import { PressProcessLineData } from "@interfaces/pressProcessLineData.interface";
import { Process } from "@interfaces/process.interface";
import { Project } from "@interfaces/project.interface";
import { PurchasableItem } from "@interfaces/purchasableItem.interface";
import { QuantityBreakLineData } from "@interfaces/quantityBreakLineData.interface";
import { QuotationAnalysisForm } from "@interfaces/quotationAnalysisForm.interface";
import { QuotationAnalysisFormDefaultSettings } from "@interfaces/quotationAnalysisFormDefaultSettings.interface";
import { RawMaterialLineData } from "@interfaces/rawMaterialLineData.interface";
import { SupportingDocumentFileLineData } from "@interfaces/supportingDocumentFileLineData.interface";
import { ToolingCostLineData } from "@interfaces/toolingCostLineData.interface";
import { ToolingFixtureLineData } from "@interfaces/toolingFixtureLineData.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { WeldAndAssemblyLineData } from "@interfaces/weldAndAssemblyLineData.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ClientService } from "@services/client.service";
import { ClientContactService } from "@services/clientContact.service";
import { CrmOpportunityService } from "@services/crmOpportunity.service";
import { FileService } from "@services/file.service";
import { MachineService } from "@services/machine.service";
import { OutsourcedSpecificationService } from "@services/outsourcedSpecification.service";
import { PackagingService } from "@services/packaging.service";
import { PartService } from "@services/part.service";
import { PaymentTermService } from "@services/paymentTerm.service";
import { ProductionToolPartService } from "@services/productionToolPart.service";
import { ProductionToolService } from "@services/productionTool.service";
import { ProcessService } from "@services/process.service";
import { ProjectService } from "@services/project.service";
import { PurchasableItemService } from "@services/purchasableItem.service";
import { QuotationAnalysisFormService } from "@services/quotationAnalysisForm.service";
import { SalesOrderService } from "@services/salesOrder.service";
import { SupplierService } from "@services/supplier.service";
import { ToolingTypeService } from "@services/toolingType.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";
import { DxDataGridComponent } from "devextreme-angular";
import { Guid } from "guid-typescript";
import { Observable, forkJoin, from, of } from "rxjs";
import { catchError, concatMap, map, switchMap, tap, toArray } from "rxjs/operators";

// Admin, engineering and quality: one-off costs of hours x an hourly rate, each amortised into the piece price
// (cost / batch size) or charged upfront. Quality's total is qualityLevelCost.
const QAF_ONE_OFF_COSTS = [
	{ amortised: "adminAmortised", cost: "adminCost", hours: "adminHours", label: "Admin", rate: "adminRate" },
	{ amortised: "engineeringAmortised", cost: "engineeringCost", hours: "engineeringHours", label: "Engineering", rate: "engineeringRate" },
	{ amortised: "qualityAmortised", cost: "qualityLevelCost", hours: "qualityHours", label: "Quality", rate: "qualityCostRate" }
];

@Component({
	moduleId: module.id,
	selector: "quotationAnalysisForms-id",
	styleUrls: ["quotationAnalysisForms-id.css"],
	templateUrl: "quotationAnalysisForms-id.html"
})
export class QuotationAnalysisFormsIdAdminComponent {
	@ViewChild("boughtOutMaterialsGridContainer", { static: false }) boughtOutMaterialsGrid!: DxDataGridComponent;
	@ViewChild(CadViewerComponent) cadViewer!: CadViewerComponent;
	@ViewChild("pressProcessesGridContainer", { static: false }) pressProcessesGrid!: DxDataGridComponent;
	@ViewChild("toolingFixturesGridContainer", { static: false }) toolingFixturesGrid!: DxDataGridComponent;
	@ViewChild("weldAndAssemblyGridContainer", { static: false }) weldAndAssemblyGrid!: DxDataGridComponent;
	MaterialQuoting = MaterialQuoting; // expose enum to template
	activeTabIndex = 0;
	boughtOutParts: BoughtOutPartsLineData[] = [];
	boughtOutPartsDocumentButtonOptions = {
		onClick: () => this.addNewSectionDocumentPopup("boughtOut"),
		text: "Add Document",
		type: "default",
		useSubmitBehavior: false
	}
	boughtOutPartsDocuments: SupportingDocumentFileLineData[] = [];
	businessSectors: any;
	cadFileButtonOptions = {
		onClick: () => this.addNewCADFilePopup(),
		text: "Add CAD File",
		type: "default",
		useSubmitBehavior: false
	}
	cadFiles: SupportingDocumentFileLineData[] = [];
	// Guards calculateQuantityBreaks against re-entry: the calculation chain it runs ends in
	// calculateFinancialSummary, which calls back into it.
	calculatingQuantityBreaks = false;
	canApproveRates = false;
	canEditMargins = false;
	changeLog: any[] = [];
	childQAFs: QuotationAnalysisForm[] = [];
	childTotals: any = { }
	clientContacts: ClientContact[] = [];
	// Everything quoted for this client, for the combined-quote picker.
	clientQAFs: QuotationAnalysisForm[] = [];
	clients: Client[] = [];
	crmOpportunities: any[] = [];
	currencies: any;
	currency = "GBP";
	defaultSettings: QuotationAnalysisFormDefaultSettings = new QuotationAnalysisFormDefaultSettings();
	// Every QAF below this one at any depth. childQAFs is only the direct children, which is what the
	// child grid and the piece price roll-up want; tooling belongs to the whole assembly, so anything
	// summing tooling has to read this instead or it silently drops grandchildren and below.
	descendantQAFs: QuotationAnalysisForm[] = [];
	expandedChildId: number | null = null;
	filteredLinkableQAFs: QuotationAnalysisForm[] = [];
	filteredParentQAFs: QuotationAnalysisForm[] = [];
	fsSections: any = { annual: false, batch: false, leadTimes: false, lifetime: false, logistics: false, margins: false, quantityBreaks: false, tooling: false };
	generatedPartId: number | null = null;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	imageStyle = "";
	incoterms: any;
	isChildQaf = false;
	isSaving = false;
	item: QuotationAnalysisForm = new QuotationAnalysisForm();
	itemType = "QAF";
	linkedQafIds: number[] = [];
	// Other QAFs included in this QAF's client quote, each with its own subtree for tooling.
	linkedQuotes: { descendants: any[]; qaf: any }[] = [];
	lockCalculations = true;
	machines: Machine[] = [];
	materialPurchasableItems: PurchasableItem[] = [];
	materialQuotingOptions = [
		{ label: "Break-Out", value: MaterialQuoting.BreakOut },
		{ label: "Fast", value: MaterialQuoting.Fast },
	];
	materialTypes: any;
	mode = "";
	outsourcedProcesses: OutsourcedProcessLineData[] = [];
	outsourcedProcessesDocumentButtonOptions = {
		onClick: () => this.addNewSectionDocumentPopup("outsourced"),
		text: "Add Document",
		type: "default",
		useSubmitBehavior: false
	}
	outsourcedProcessesDocuments: SupportingDocumentFileLineData[] = [];
	outsourcedSpecifications: OutsourcedSpecification[] = [];
	packaging: Packaging[] = [];
	packagingTerms: any;
	pageLoadedAt = new Date;
	parentQAFs: QuotationAnalysisForm[] = [];
	paymentTerms: PaymentTerm[] = [];
	popupAddButtonOptions: any;
	popupCADFile: SupportingDocumentFileLineData = new SupportingDocumentFileLineData();
	popupClient: Client = new Client();
	popupClientPdf: any = { toolingView: "itemised" };
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupContact: ClientContact = new ClientContact();
	popupCreateSalesOrderFromPart: CreateSalesOrderFromPart = new CreateSalesOrderFromPart();
	popupGenerateAndDownloadQuotationAnalysisFormPDFDto: GenerateAndDownloadQuotationAnalysisFormPDFDto = new GenerateAndDownloadQuotationAnalysisFormPDFDto();
	popupImage: SupportingDocumentFileLineData = new SupportingDocumentFileLineData();
	popupProject: Project = new Project();
	popupPurchasableItem: PurchasableItem = new PurchasableItem();
	popupSaveButtonOptions: any;
	popupSectionDocument: SupportingDocumentFileLineData = new SupportingDocumentFileLineData();
	popupSectionDocumentSection: "boughtOut" | "outsourced" = "boughtOut";
	popupSupplier: Supplier = new Supplier();
	popupSupportingDocument: SupportingDocumentFileLineData = new SupportingDocumentFileLineData();
	popupTitle = "";
	popupVisible = false;
	pressProcesses: PressProcessLineData[] = [];
	processes: Process[] = [];
	projects: Project[] = [];
	purchasableItems: PurchasableItem[] = [];
	purchaseTypes: any;
	quantityBreaks: QuantityBreakLineData[] = [];
	rawMaterial: RawMaterialLineData = new RawMaterialLineData();
	readOnly = true;
	recalculatingChild = false;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	suppliers: Supplier[] = [];
	supportingDocumentButtonOptions = {
		onClick: () => this.addNewSupportingDocumentPopup(),
		text: "Add Document",
		type: "default",
		useSubmitBehavior: false
	}
	supportingDocuments: SupportingDocumentFileLineData[] = [];
	tabPanelOptions: any = {
		hoverStateEnabled: false,
		onSelectionChanged: (e: any) => { this.activeTabIndex = e.component.option("selectedIndex"); this.tabPanelOptions.selectedIndex = this.activeTabIndex; },
		scrollByContent: true,
		selectedIndex: 0
	};
	thicknesses: number[] = environment.build.thicknesses;
	title: string = "View " + this.itemType;
	toolingCosts: ToolingCostLineData[] = [];
	toolingFixtureDescriptions: any;
	toolingFixtures: ToolingFixtureLineData[] = [];
	toolingTypes: ToolingType[] = [];
	userProfiles: UserProfile[] = [];
	weldAndAssembly: WeldAndAssemblyLineData[] = [];

	constructor(private router:Router, private toastService: ToastService, private route: ActivatedRoute, appSettingsService: AppSettingsService, private clientService: ClientService, private clientContactService: ClientContactService, private fileService: FileService, private machineService: MachineService, private outsourcedSpecificationService: OutsourcedSpecificationService, private packagingService: PackagingService, private paymentTermsService: PaymentTermService, private processService: ProcessService, private projectService: ProjectService, private purchasableItemService: PurchasableItemService, private partService: PartService, private qafService: QuotationAnalysisFormService, private salesOrderService: SalesOrderService, private supplierService: SupplierService, private userProfileService: UserProfileService, private productionToolService: ProductionToolService, private toolingTypeService: ToolingTypeService, private productionToolPartService: ProductionToolPartService, private crmOpportunityService: CrmOpportunityService) {
		this.canEditMargins = CheckGroupPermissions(appSettingsService, "Super Admin") || CheckGroupPermissions(appSettingsService, "Executive");
		this.canApproveRates = CheckGroupPermissions(appSettingsService, "Super Admin");

		this.route.params
			.subscribe((params) => {
				if (params.id != "create") {
					if (params.id.split("_").length > 1) {
						this.getItem(params.id.split("_")[0], "edit");
					} else {
						this.getItem(params.id, "view");
					}
				} else {
					// A new QAF can be seeded with starting values stashed in sessionStorage by whoever sent us
					// here - "New QAF" on a CRM opportunity sets the client and the opportunity. qafChildSeed is
					// the older key from the parent QAF's "Add Child" flow.
					const seedKey = sessionStorage.getItem("qafSeed") ? "qafSeed" : "qafChildSeed";
					const seedJson = sessionStorage.getItem(seedKey);
					if (seedJson) {
						sessionStorage.removeItem(seedKey);
						const seed = JSON.parse(seedJson);
						Object.keys(seed).forEach((k) => {
							if (seed[k] !== undefined && seed[k] !== null) { (this.item as any)[k] = seed[k]; }
						});
						// Setting clientId here doesn't fire the form's change handler, so load its lists directly.
						if (this.item.clientId) {
							this.getClientContacts();
							this.getCrmOpportunities();
							this.getOutsourcedSpecifications();
							this.getProjects();
						}
						this.filterParentQAFs();
					}
					this.buildButtons("create");
				}
			});

		this.businessSectors = GetPropertiesFromEnum(BusinessSector);
		this.currencies = GetPropertiesFromEnum(Currency, true);
		this.incoterms = GetPropertiesFromEnum(Incoterms, true);
		this.materialTypes = GetPropertiesFromEnum(MaterialType);
		this.packagingTerms = GetPropertiesFromEnum(PackagingTerms);
		this.purchaseTypes = GetPropertiesFromEnum(PurchaseTypeEnum);
		this.toolingFixtureDescriptions = GetPropertiesFromEnum(ToolingFixtureDescription);

		this.getClients();
		this.getDefaultSettings();
		this.getMachines();
		this.getPackaging();
		this.getParentQAFs();
		this.getPaymentTerms();
		this.getProcesses();
		this.getPurchasableItems();
		this.getToolingTypes();
		this.getSuppliers();
		this.getUserProfiles();

		this.addChildQAF = this.addChildQAF.bind(this);
		this.generateChildQaf = this.generateChildQaf.bind(this);
		this.downloadFileLink = this.downloadFileLink.bind(this);
		this.inspectCADFile = this.inspectCADFile.bind(this);
		this.isCadFileInspectable = this.isCadFileInspectable.bind(this);
		this.mainFormDataChanged = this.mainFormDataChanged.bind(this);
		this.rawMaterialFormDataChanged = this.rawMaterialFormDataChanged.bind(this);
		this.removeChildQAF = this.removeChildQAF.bind(this);
		this.uploadCADFile = this.uploadCADFile.bind(this);
		this.uploadImage = this.uploadImage.bind(this);
		this.uploadSupportingDocument = this.uploadSupportingDocument.bind(this);
		this.viewChildQAF = this.viewChildQAF.bind(this);
		this.duplicatePressProcessLine = this.duplicatePressProcessLine.bind(this);
		this.updateRouterOrder = this.updateRouterOrder.bind(this);
		this.deleteCADFileLink = this.deleteCADFileLink.bind(this);
		this.deleteSupportingDocumentLink = this.deleteSupportingDocumentLink.bind(this);
		this.boughtOutLineTotalWithMargin = this.boughtOutLineTotalWithMargin.bind(this);
		this.createOutsourcedSpecification = this.createOutsourcedSpecification.bind(this);
		this.deleteSectionDocumentLink = this.deleteSectionDocumentLink.bind(this);
		this.uploadSectionDocument = this.uploadSectionDocument.bind(this);
	}

	addChildQAF() {
		if (!this.item.id) {
			this.toastService.error("Save this QAF before adding a child.");
			return;
		}
		// Create a blank child in place: copy only the parent's Main Details (no pricing or line data),
		// link it to this QAF, and refresh the inline list without leaving the master. partNumber is
		// carried over so the API's non-empty rule passes; the user renames it in the inline editor.
		const fields = ["annualVolume", "batchSize", "businessSector", "clientContactId", "clientId", "enquiryDate", "partDescription", "partNumber", "projectId", "projectLengthYears", "revisionLevel"];
		const src = this.item as any;
		const child: any = { approved: false, parentQuotationAnalysisFormId: this.item.id, partNumberExists: false };
		fields.forEach((f) => { child[f] = src[f]; });
		this.qafService.createSingle(child)
			.subscribe(
				() => {
					this.toastService.success("Blank child QAF created");
					this.getChildQAFs();
				},
				(err) => {
					console.log("Create blank child QAF failed", err);
					this.toastService.error(err?.error?.error || err?.error?.message || err?.message || "Could not create child QAF");
				}
			);
	}

	addNewCADFile() {
		if (this.popupCADFile.description === "") {
			alert("Please enter a description");
			return;
		}
		this.cadFiles.push(this.popupCADFile);
		this.popupCADFile = new SupportingDocumentFileLineData();
		this.closePopup();
	}

	addNewCADFilePopup() {
		this.popupCADFile = new SupportingDocumentFileLineData();
		this.popupTitle = "Add New CAD File";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewCADFile(), text: "Add CAD File" };
	}

	addNewClient() {
		this.clientService.createSingle(this.popupClient)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupClient = new Client();
					this.getClients();
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Could not add client. Please check the details are valid.");
				}
			);
	}

	addNewClientPopup() {
		this.popupClient = new Client();
		this.popupTitle = "Add New Client";
		this.popupVisible = true;
		this.popupSaveButtonOptions = { onClick: () => this.addNewClient(), text: "Add Client", visible: true };
	}

	addNewContact() {
		this.clientContactService.createSingle(this.popupContact)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupContact = new ClientContact();
					this.getClientContacts();
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Could not add contact. Please check the details are valid.");
				}
			);
	}

	addNewContactPopup() {
		this.popupContact = new ClientContact();
		this.popupContact.clientId = this.item.clientId;
		this.popupTitle = "Add New Contact";
		this.popupVisible = true;
		this.popupSaveButtonOptions = { onClick: () => this.addNewContact(), text: "Add Contact", visible: true };
	}

	addNewProject() {
		this.projectService.createSingle(this.popupProject)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupProject = new Project();
					this.getProjects();
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Could not add project. Please check the details are valid.");
				}
			);
	}

	addNewProjectPopup() {
		this.popupProject = new Project();
		this.popupProject.clientId = this.item.clientId;
		this.popupTitle = "Add New Project";
		this.popupVisible = true;
		this.popupSaveButtonOptions = { onClick: () => this.addNewProject(), text: "Add Project", visible: true };
	}

	addNewPurchasableItem() {
		this.purchasableItemService.createSingle(this.popupPurchasableItem)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupPurchasableItem = new PurchasableItem();
					this.getPurchasableItems(); // refresh so the new item is selectable in the material / bought-out grids
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Could not add purchasable item. Please check the details are valid.");
				}
			);
	}

	addNewPurchasableItemPopup() {
		this.popupPurchasableItem = new PurchasableItem();
		this.popupTitle = "Add New Purchasable Item";
		this.popupVisible = true;
		this.popupSaveButtonOptions = { onClick: () => this.addNewPurchasableItem(), text: "Add Item", visible: true };
	}

	// Documents on the BOM and outsourced process tabs (supplier quotes, paint codes). Kept apart from the
	// QAF's supporting documents because approval requires a supporting document - a material quote
	// shouldn't be able to satisfy that.
	addNewSectionDocument() {
		if (this.popupSectionDocument.description === "") {
			alert("Please enter a description");
			return;
		}
		if (this.popupSectionDocument.lookupId === "") {
			alert("Please upload a file");
			return;
		}
		this.sectionDocuments(this.popupSectionDocumentSection).push(this.popupSectionDocument);
		this.popupSectionDocument = new SupportingDocumentFileLineData();
		this.closePopup();
	}

	addNewSectionDocumentPopup(section: "boughtOut" | "outsourced") {
		this.popupSectionDocument = new SupportingDocumentFileLineData();
		this.popupSectionDocumentSection = section;
		this.popupTitle = "Add New Document";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewSectionDocument(), text: "Add Document" };
	}

	addNewSupplier() {
		this.supplierService.createSingle(this.popupSupplier)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.popupSupplier = new Supplier();
					this.getSuppliers();
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Could not add supplier. Please check the details are valid.");
				}
			);
	}

	addNewSupplierPopup() {
		this.popupSupplier = new Supplier();
		this.popupTitle = "Add New Supplier";
		this.popupVisible = true;
		this.popupSaveButtonOptions = { onClick: () => this.addNewSupplier(), text: "Add Supplier", visible: true };
	}

	addNewSupportingDocument() {
		if (this.popupSupportingDocument.description === "") {
			alert("Please enter a description");
			return;
		}
		this.supportingDocuments.push(this.popupSupportingDocument);
		this.popupSupportingDocument = new SupportingDocumentFileLineData();
		this.closePopup();
	}

	addNewSupportingDocumentPopup() {
		this.popupSupportingDocument = new SupportingDocumentFileLineData();
		this.popupTitle = "Add New Supporting Document";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewSupportingDocument(), text: "Add Document" };
	}

	addTool() {
		const tool = new ToolingCostLineData();
		tool.id = Guid.create().toString();
		tool.name = "Tool " + (this.toolingCosts.length + 1);
		tool.quoteType = "fast";
		tool.designRate = this.defaultSettings.designRate;
		tool.autoFormRate = this.defaultSettings.autoFormRate;
		tool.machineRate = this.defaultSettings.machineRate;
		tool.assemblyRate = this.defaultSettings.assemblyRate;
		tool.qualityRate = this.defaultSettings.qualityRate;
		tool.defaultDesignRate = tool.designRate;
		tool.defaultAutoFormRate = tool.autoFormRate;
		tool.defaultMachineRate = tool.machineRate;
		tool.defaultAssemblyRate = tool.assemblyRate;
		tool.defaultQualityRate = tool.qualityRate;
		tool.expanded = true;
		this.toolingCosts.push(tool);
		this.calculateToolingCosts();
	}

	// Highlights a cost in the summary by whether it's amortised into the piece price.
	amortisedClass(cost?: number, amortised?: boolean): string {
		if (!cost) return "";
		return amortised ? "tooling-amortised-all" : "tooling-amortised-partial";
	}

	approve() {
		if (confirm("Approve the changed rates on this QAF? Changing a rate again will need a new approval.") === false) {
			return;
		}

		this.qafService.approve(this.item.id)
			.subscribe(
				() => {
					this.toastService.success("Successfully Approved " + this.itemType);
					this.getItem(this.item.id, this.mode);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	boughtOutLineTotalWithMargin(rowData: any): number {
		// Line total including the per-line margin (matches how the bought-out subtotal is built).
		return (rowData.total || 0) * (1 + (rowData.marginPercentage || 0) / 100);
	}

	buildButtons(mode: string) {
		this.mode = mode;
		this.headerPrimaryButtons = [];
		this.headerTertiaryButtons = [];
		switch (mode) {
			case "create":
				this.title = "Create new " + this.itemType;
				this.readOnly = false;
				this.headerPrimaryButtons = [
					{ method: "createItem", text: "Save" },
					{ method: "close", text: "Close" }
				];
				this.headerTertiaryButtons = [
					{ method: "addNewClientPopup", text: "Add Customer" },
					{ method: "addNewContactPopup", text: "Add Contact" },
					{ method: "addNewProjectPopup", text: "Add Project" },
					{ method: "addNewSupplierPopup", text: "Add Supplier" },
					{ method: "addNewPurchasableItemPopup", text: "Add Purchasable Item" }
				];
				break;
			case "edit":
				this.title = "Edit " + this.itemType + " #" + this.item.id;
				this.readOnly = false;
				this.headerPrimaryButtons = [
					{ method: "updateItem", text: "Save Changes" },
					{ method: "cancelEditing", text: "View Mode" },
					{ method: "close", text: "Close" }
				];
				this.headerTertiaryButtons = [
					{ method: "duplicateQaf", text: "Duplicate QAF" },
					{ method: "addNewClientPopup", text: "Add Customer" },
					{ method: "addNewContactPopup", text: "Add Contact" },
					{ method: "addNewProjectPopup", text: "Add Project" },
					{ method: "addNewSupplierPopup", text: "Add Supplier" },
					{ method: "addNewPurchasableItemPopup", text: "Add Purchasable Item" }
				];
				break;
			case "view":
				this.title = "View " + this.itemType + " #" + this.item.id;
				this.readOnly = true;
				this.headerPrimaryButtons = [
					{ method: "edit", text: "Edit" },
					{ method: "close", text: "Close" }
				];
				if (this.item.rateApprovalRequired && this.item.approved === false && this.canApproveRates) {
					this.headerTertiaryButtons = [
						{ method: "approve", text: "Approve" }
					];
				}
				// The front-end client PDF replaces the old server-generated one, so it takes the label.
				this.headerTertiaryButtons.push({ method: "clientPdfPopup", text: "Download PDF" });
				this.headerTertiaryButtons.push({ method: "duplicateQaf", text: "Duplicate QAF" });
				break;
		}
	}

	// Builds a self-contained, client-facing quotation document (same data as the server PDF plus a
	// tooling section) for printing / save-as-PDF from the browser.
	buildClientPdfHtml(toolingView: string) {
		// The quote is the one place these are read by a customer, so they are worked out again rather
		// than trusted to be current.
		this.calculateQuantityBreaks();
		const item = this.item;
		const client: any = this.clients.find(c => c.id === item.clientId);
		const contact: any = this.clientContacts.find(c => c.id === item.clientContactId);
		const paymentTerm = client ? (this.paymentTerms.find(p => p.id === client.paymentTermId) || { termCode: "" }).termCode : "";
		const material: any = this.materialPurchasableItems.find(pi => pi.id === this.rawMaterial.purchasableItemId);
		const sym = item.currency === Currency.EUR ? "€" : "£";
		const money = (n: number) => sym + (n || 0).toLocaleString("en-GB", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
		const esc = (s: any) => (s == null ? "" : String(s)).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
		const quoteNo = String(item.id).padStart(6, "0");
		const fixtureName = (d: number) => { const f = (this.toolingFixtureDescriptions || []).find((x: any) => x.id === d); return f ? f.name : "Fixture"; };

		// The client is buying the full assembly, so tooling from every QAF beneath this one belongs on
		// the quotation alongside the parent's - grandchildren included, not just the direct children.
		// Parsed from the stored JSON rather than each child's parsed* arrays, which are only populated
		// once that child's row has been expanded in the UI.
		const childTools: any[] = [];
		const childFixtures: any[] = [];
		const collectTooling = (qaf: any) => {
			try {
				JSON.parse(qaf.toolingCostLineData || "[]").forEach((t: any) => childTools.push({ ...t, partNumber: qaf.partNumber }));
				JSON.parse(qaf.toolingFixtureLineData || "[]").forEach((f: any) => childFixtures.push({ ...f, partNumber: qaf.partNumber }));
			} catch (e) {
				console.log("Could not parse tooling line data for QAF " + qaf.id, e);
			}
		};
		this.getToolingScopeQAFs().forEach(collectTooling);

		// QAFs combined into this quote: each linked QAF plus its own subtree, treated like this QAF's own.
		const linkedQafs = this.linkedQuotes.map(l => l.qaf).filter(q => !!q);
		const linkedAll = this.linkedQuotes.reduce((all: any[], l) => [...all, ...(l.qaf ? [l.qaf] : []), ...l.descendants], []);
		linkedAll.forEach(collectTooling);

		// An amortised tool or fixture is recovered through the piece price (cost / batch size, added to
		// totalPartCost and so to batchPrice), so it must not also be charged separately - that billed it
		// twice. Only non-amortised lines are charged on top. How a cost is recovered is BPL's business, so
		// the client quote never says which lines are amortised or names them as charged upfront.
		const allTools = [...this.toolingCosts, ...childTools];
		const allFixtures = [...this.toolingFixtures, ...childFixtures];
		const sum = (lines: any[], cost: (l: any) => number) => lines.reduce((n, l) => n + (cost(l) || 0), 0);
		const toolingTotal = sum(allTools.filter(t => !t.amortised), t => t.toolingTotalCost);
		const fixtureTotal = sum(allFixtures.filter(f => !f.amortised), f => f.total);
		// Admin, engineering and quality are one-off costs treated like tooling: amortised, they're in the
		// piece price; otherwise they're charged upfront.
		const oneOffs: any[] = [item, ...this.getToolingScopeQAFs(), ...linkedAll];
		const oneOffTotals = QAF_ONE_OFF_COSTS.map(c => ({
			label: c.label,
			upfront: sum(oneOffs.filter(q => !q[c.amortised]), q => q[c.cost])
		}));
		const oneOffUpfrontTotal = oneOffTotals.reduce((n, c) => n + c.upfront, 0);
		const oneOffRows = (bold: boolean) => {
			const b = (t: string) => (bold ? `<b>${t}</b>` : t);
			return oneOffTotals.filter(c => c.upfront > 0)
				.map(c => `<tr><td>${b(c.label)}</td><td class="r">${b(money(c.upfront))}</td></tr>`)
				.join("");
		};
		const hasTooling = this.toolingCosts.length > 0 || this.toolingFixtures.length > 0
			|| childTools.length > 0 || childFixtures.length > 0 || toolingTotal > 0 || fixtureTotal > 0
			|| oneOffUpfrontTotal > 0;

		let toolingHtml = "";
		if (toolingView === "itemised") {
			// With children the rows span several parts, so name the part each one belongs to. A QAF with
			// no children reads better without the repetition.
			const showPart = childTools.length > 0 || childFixtures.length > 0;
			const part = (p: string) => (showPart && p ? ` <span class="sub">${esc(p)}</span>` : "");
			// Only the lines the client is being charged for: an amortised one is already inside the piece price.
			const toolRow = (t: any, p: string) => `<tr><td>${esc(t.name || "Tool")}${t.stageNumber ? " (Stage " + t.stageNumber + ")" : ""}${part(p)}</td><td class="r">${money(t.toolingTotalCost)}</td></tr>`;
			const fixRow = (f: any, p: string) => `<tr><td>${esc(fixtureName(f.description))}${part(p)}</td><td class="r">${money(f.total)}</td></tr>`;

			const rows = this.toolingCosts.filter(t => !t.amortised).map(t => toolRow(t, item.partNumber)).join("")
				+ this.toolingFixtures.filter(f => !f.amortised).map(f => fixRow(f, item.partNumber)).join("")
				+ childTools.filter(t => !t.amortised).map(t => toolRow(t, t.partNumber)).join("")
				+ childFixtures.filter(f => !f.amortised).map(f => fixRow(f, f.partNumber)).join("");

			toolingHtml = `<table class="tbl"><thead><tr><th>Tooling / Fixture</th><th class="r">Cost</th></tr></thead>
				<tbody>${rows || "<tr><td colspan=\"2\">None</td></tr>"}</tbody>
				<tfoot><tr><td><b>Total Tooling</b></td><td class="r"><b>${money(toolingTotal)}</b></td></tr>
				<tr><td><b>Total Fixtures</b></td><td class="r"><b>${money(fixtureTotal)}</b></td></tr>
				${oneOffRows(true)}</tfoot></table>`;
		} else {
			toolingHtml = `<table class="tbl"><tbody>
				<tr><td>Total Tooling Costs</td><td class="r">${money(toolingTotal)}</td></tr>
				<tr><td>Total Fixture Costs</td><td class="r">${money(fixtureTotal)}</td></tr>
				${oneOffRows(false)}</tbody></table>`;
		}

		const hasAdditionalNotes = this.item.additionalNotes.length > 0;

		// Packaging & logistics is part of what a piece costs, not a line of its own on the quote, so it goes
		// into the piece price (and so into the batch total) rather than being listed separately.
		const piecePrice = (q: any) => (q.totalPartCost || 0) + (q.packagingAndLogistics || 0);
		const batchTotal = (q: any) => piecePrice(q) * (q.batchSize || 0);
		// One-off costs the client is charged for (tooling, fixtures, admin, engineering, quality) fold into
		// the sub total, and so into VAT and the grand total. Amortised ones are already in the piece price.
		// Price breaks, cheapest quantity first. Packaging & logistics is added per piece the same way as
		// the row above, so a break price is directly comparable with the quoted batch price.
		const breaks = (this.quantityBreaks || []).filter(b => (b.quantity || 0) > 0).sort((a, b) => a.quantity - b.quantity);
		const breakPiecePrice = (b: any) => (b.piecePrice || 0) + (item.packagingAndLogistics || 0);
		const breaksHtml = breaks.length === 0 ? "" : `<div class="section">Price Breaks</div>
				<table class="tbl">
					<thead><tr><th>Order Quantity</th><th class="r">Piece Price</th><th class="r">Total</th></tr></thead>
					<tbody>${breaks.map(b => `<tr><td>${b.quantity} off</td><td class="r">${money(breakPiecePrice(b))}</td><td class="r">${money(breakPiecePrice(b) * b.quantity)}</td></tr>`).join("")}</tbody>
				</table>
				<div class="chips"><div class="chip">Prices shown for <b>${esc(item.partNumber)}</b></div><div class="chip">Tooling and other one-off costs are charged as listed</div></div>`;
		const partsTotal = batchTotal(item) + sum(linkedQafs, q => batchTotal(q));
		const subTotal = partsTotal + toolingTotal + fixtureTotal + oneOffUpfrontTotal;
		const vat = client && client.applyVAT ? subTotal * 0.2 : 0;
		const grand = subTotal + vat;
		const custAddress = client ? [contact ? esc(contact.contactName) : "", esc(client.name), esc(client.address1), esc(client.address2), esc(client.town), esc(client.postcode)].filter(Boolean).join("<br>") : "";
		const subTotalRow = client && client.applyVAT ? `<tr><td>Sub Total</td><td class="r">${money(subTotal)}</td></tr>` : "";
		const vatRow = client && client.applyVAT ? `<tr><td>VAT (20%)</td><td class="r">${money(vat)}</td></tr>` : "";

		return `<!doctype html><html><head><meta charset="utf-8"><title>Quotation ${quoteNo}</title>
		<style>
			:root { --brand: #344281; --brand-2: #4a5bb0; --ink: #1f2430; --muted: #6b7280; --line: #e7e9f0; --bg: #f6f7fb; }
			* { box-sizing: border-box; }
			body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; color: var(--ink); margin: 0; background: #fff; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
			.page { max-width: 820px; margin: 0 auto; padding: 40px 40px; min-height: 100vh; box-sizing: border-box; display: flex; flex-direction: column; }
			.header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 20px; border-bottom: 3px solid var(--brand); margin-bottom: 26px; }
			.header .logo { height: 58px; }
			.totals-row { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; margin-top: 10px; }
			.totals-row .totals { margin: 0; }
			.part-image img { max-height: 120px; max-width: 200px; border: 1px solid var(--line); border-radius: 6px; }
			.header .meta { text-align: right; }
			.header .meta .label { font-size: 26px; font-weight: 300; letter-spacing: 4px; color: var(--brand); }
			.header .meta .num { font-size: 16px; font-weight: 700; color: var(--ink); margin-top: 2px; }
			.header .meta .terms { font-size: 11px; color: var(--muted); margin-top: 6px; }
			.cards { display: flex; gap: 16px; margin-bottom: 26px; }
			.card { flex: 1; background: var(--bg); border: 1px solid var(--line); border-radius: 10px; padding: 14px 16px; }
			.card h3 { margin: 0 0 6px; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--brand); font-weight: 700; }
			.card .body { line-height: 1.5; color: var(--ink); }
			.section { font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--brand); font-weight: 700; margin: 26px 0 8px; }
			.tbl { width: 100%; border-collapse: collapse; }
			.tbl th { text-align: left; font-size: 10px; letter-spacing: .5px; text-transform: uppercase; color: var(--muted); padding: 8px 12px; border-bottom: 2px solid var(--brand); }
			.tbl td { padding: 9px 12px; border-bottom: 1px solid var(--line); }
			.tbl tr:nth-child(even) td { background: #fafbfe; }
			.tbl .r { text-align: right; } .tbl .c { text-align: center; }
			.sub { color: var(--muted); font-size: 10px; }
			.meta-row td { color: var(--muted); font-size: 11px; }
			.chips { margin: 10px 0 0; display: flex; gap: 10px; flex-wrap: wrap; }
			.chip { background: var(--bg); border: 1px solid var(--line); border-radius: 20px; padding: 5px 12px; font-size: 11px; }
			.chip b { color: var(--brand); }
			.totals { width: 320px; margin-left: auto; margin-top: 10px; }
			.totals td { padding: 7px 12px; }
			.totals .grand td { border-top: 2px solid var(--brand); font-weight: 700; font-size: 15px; color: var(--brand); }
			.terms { margin-top: 34px; font-size: 9px; color: var(--muted); text-align: center; border-top: 1px solid var(--line); padding-top: 12px; line-height: 1.6; }
			.page-footer { margin-top: auto; padding-top: 16px; font-size: 9px; color: var(--muted); text-align: center; border-top: 1px solid var(--line); line-height: 1.5; }
			@media print { .hero { -webkit-print-color-adjust: exact; } @page { margin: 12mm; } }
		</style></head>
		<body onload="window.print()">
			<div class="page">
				<div class="header">
					<img class="logo" src="${location.origin}/assets/Logo.png" onerror="this.style.display='none'"/>
					<div class="meta">
						<div class="label">QUOTATION</div>
						<div class="num">#${quoteNo}</div>
						<div class="terms">Payment Terms: ${esc(paymentTerm) || "&mdash;"}</div>
					</div>
				</div>
				<div class="cards">
					<div class="card"><h3>From</h3><div class="body"><b>BPL Engineering Group Ltd.</b><br>5 Catesby Park, Kings Norton<br>Birmingham, B38 8SE<br>0121 725 2224 &bull; info@bpl-group.co.uk<br><span style="color:var(--muted); font-size:10px;">Registered in England No: 5090486 &bull; VAT Reg. No: 837 4099 02</span></div></div>
					<div class="card"><h3>Prepared For</h3><div class="body">${custAddress || "&mdash;"}</div></div>
				</div>

				<div class="section">Part &amp; Pricing</div>
				<table class="tbl">
					<thead><tr><th>Description</th><th class="r">Qty (Batch)</th><th class="r">Piece Price</th><th class="r">Batch Total</th></tr></thead>
					<tbody>
						<tr><td><b>${esc(item.partNumber)}</b></td><td class="r">${item.batchSize}</td><td class="r">${money(piecePrice(item))}</td><td class="r">${money(batchTotal(item))}</td></tr>
						<tr class="meta-row"><td colspan="4">${esc(item.partDescription)}</td></tr>
						${linkedQafs.map(q => `<tr><td><b>${esc(q.partNumber)}</b></td><td class="r">${q.batchSize}</td><td class="r">${money(piecePrice(q))}</td><td class="r">${money(batchTotal(q))}</td></tr>
						<tr class="meta-row"><td colspan="4">${esc(q.partDescription)}</td></tr>`).join("")}
					</tbody>
				</table>
				${breaksHtml}
				<div class="chips">
					${item.annualVolume > 0 ? `<div class="chip">Annual Qty <b>${item.annualVolume}</b></div>` : ""}
					${material ? `<div class="chip">Material <b>${esc(material.name)}</b></div>` : ""}
					${item.leadTimeFirstOff > 0 ? `<div class="chip">Lead (first off) <b>${item.leadTimeFirstOff} wks</b></div>` : ""}
					${item.leadTimePhaseThree > 0 ? `<div class="chip">Lead (phase 3) <b>${item.leadTimePhaseThree} wks</b></div>` : ""}
				</div>

				${hasTooling ? `<div class="section">Tooling</div>${toolingHtml}` : ""}

				<div class="totals-row">
					${item.partImageUrl && item.partImageUrl.length > 2 ? `<div class="part-image"><img src="${esc(item.partImageUrl)}" onerror="this.parentNode.style.display='none'"/></div>` : "<div></div>"}
					<table class="tbl totals"><tbody>
						${subTotalRow}
						${vatRow}
						<tr class="grand"><td>Grand Total</td><td class="r">${money(grand)}</td></tr>
					</tbody></table>
				</div>

				${hasAdditionalNotes ? `<div class="section">Additional Notes</div><p>${this.item.additionalNotes}</p>` : ""}

				<div class="page-footer">
					Quotation is valid for 30 days, subject to Jaguar Land Rover terms and conditions of sale.<br>
					Tooling will be kept at BPL Engineering Group for 3 months from delivery completion. Tooling will be available for collection over a period of 1 month or recycled.<br>
					Registered in England No: 5090486 &nbsp;&bull;&nbsp; VAT Reg. No: 837 4099 02
				</div>
			</div>
		</body></html>`;
	}

	calculateBoughtOutMaterialsLineData() {
		let boughtOutPartsSubtotal = 0;
		let boughtOutMarginCost = 0;

		this.boughtOutParts.forEach((line) => {
			line.total = line.quantity * line.costPerPart;
			line.overhead = line.total * ((line.marginPercentage || 0) / 100);
			// Margin% on each line adds onto the line's net cost contribution.
			boughtOutPartsSubtotal += line.total + line.overhead;
			boughtOutMarginCost += line.overhead;
		});

		this.item.boughtOutPartsSubtotal = boughtOutPartsSubtotal;
		this.item.boughtOutPartsOverhead = boughtOutMarginCost;
		this.calculateFinancialSummary();
	}

	calculateFinancialSummary() {
		const rawMat = this.item.rawMaterialSubtotal || 0;
		const boughtOut = this.item.boughtOutPartsSubtotal || 0;
		const outsourced = this.item.outsourcedProcessesSubtotal || 0;
		const press = this.item.pressProcessSubtotal || 0;
		const weld = this.item.weldAndAssemblySubtotal || 0;

		// Material margin is entered per-line on the Materials tab and is baked into rawMaterialSubtotal
		// (net = gross + overhead). Surface it as a separate margin on the summary: take the base as the
		// gross (net − overhead) so the margin isn't double-counted, and the % is the effective rate.
		const materialMargin = this.item.materialOverhead || 0;
		const rawMatBase = rawMat - materialMargin;
		this.item.rawMaterialMarginCost = materialMargin;
		this.item.rawMaterialMarginPercentage = rawMatBase > 0 ? (materialMargin / rawMatBase) * 100 : 0;

		// Bought-out margin is entered per-line and baked into boughtOutPartsSubtotal (net); surface it
		// separately with a gross base so it isn't double-counted, same as material.
		const boughtOutMargin = this.item.boughtOutPartsOverhead || 0;
		const boughtOutBase = boughtOut - boughtOutMargin;
		this.item.boughtOutMarginCost = boughtOutMargin;
		this.item.boughtOutMarginPercentage = boughtOutBase > 0 ? (boughtOutMargin / boughtOutBase) * 100 : 0;
		// Outsourced margin is set per-row in the Outsourced Processes grid; the Financial Summary
		// just displays the summed line margins and the effective percentage they work out to.
		this.item.outsourcedMarginCost = this.item.outsourcedOverhead || 0;
		this.item.outsourcedMarginPercentage = outsourced > 0 ? (this.item.outsourcedMarginCost / outsourced) * 100 : 0;
		this.item.processMarginCost = (press + weld) * ((this.item.processMarginPercentage || 0) / 100);
		// Tooling margin is set per-tool; the summary displays the summed tool margins and effective %.
		const tooling = this.item.totalToolingCosts || 0;
		this.item.toolingMarginCost = this.toolingCosts.reduce((sum, t) => sum + (t.toolingTotalCost * ((t.marginPercentage || 0) / 100)), 0);
		this.item.toolingMarginPercentage = tooling > 0 ? (this.item.toolingMarginCost / tooling) * 100 : 0;

		const allBaseCosts = rawMatBase + boughtOutBase + outsourced + press + weld;
		this.item.profit = allBaseCosts * ((this.item.profitMarginPercentage || 0) / 100);

		const allMargins = this.item.rawMaterialMarginCost + this.item.boughtOutMarginCost + this.item.outsourcedMarginCost + this.item.processMarginCost + this.item.toolingMarginCost + this.item.profit;
		this.item.additionalCostsSubtotal = allMargins;

		// Piece price (totalPartCost): base costs + margins — tooling NOT included in piece price
		this.item.totalPartCost = allBaseCosts + allMargins;

		// Amortise tooling into piece cost if requested
		this.toolingCosts.forEach((tool) => {
			if (tool.amortised && this.item.batchSize > 0) {
				this.item.totalPartCost += (tool.toolingTotalCost || 0) / this.item.batchSize;
			}
		});

		// Amortise fixtures into piece cost if requested
		this.toolingFixtures.forEach((fixture) => {
			if (fixture.amortised && this.item.batchSize > 0) {
				this.item.totalPartCost += (fixture.total || 0) / this.item.batchSize;
			}
		});

		// Amortise admin, engineering and quality costs if requested: (rate x hours) / batch size each
		QAF_ONE_OFF_COSTS.forEach((c) => {
			if ((this.item as any)[c.amortised]) {
				this.item.totalPartCost += this.perPiece((this.item as any)[c.cost]);
			}
		});

		// The parent QAF's piece price includes the summed piece prices of its child QAFs. Skipped while
		// recomputing an individual child (recalculatingChild) so a child keeps its own piece price.
		if (this.recalculatingChild === false) {
			this.item.totalPartCost += this.childTotals.totalPartCost || 0;
			// Group margin: an uplift (% of the combined total, plus a fixed amount) layered on top of
			// the parent + children piece price. The parent's and children's own figures are untouched.
			this.item.groupMarginCost = this.item.totalPartCost * ((this.item.groupMarginPercentage || 0) / 100);
			this.item.totalPartCost += this.item.groupMarginCost + (this.item.groupMarginAmount || 0);
		}

		const piecePrice = this.item.totalPartCost || 0;
		const grandTotalBase = piecePrice + (this.item.packagingAndLogistics || 0) + (this.item.containerCost || 0);
		this.item.grandTotal = grandTotalBase;

		// Batch calculations
		const batchCostBase = rawMatBase + boughtOutBase + outsourced + press;
		this.item.batchPrice = piecePrice * (this.item.batchSize || 0);
		this.item.batchCost = batchCostBase * (this.item.batchSize || 0);
		this.item.batchValueAdded = this.item.batchPrice - this.item.batchCost;

		// Annual calculations
		const annualVol = this.item.annualVolume || 0;
		this.item.annualPrice = piecePrice * annualVol;
		this.item.annualCost = batchCostBase * annualVol;
		this.item.annualValueAdded = this.item.annualPrice - this.item.annualCost;

		// Lifetime calculations
		const projectLengthYears = this.item.projectLengthYears || 0;
		this.item.lifetimePrice = this.item.annualPrice * projectLengthYears;
		this.item.lifetimeCost = this.item.annualCost * projectLengthYears;
		this.item.lifetimeAddedValue = this.item.lifetimePrice - this.item.lifetimeCost;

		// Every price break is derived from the figures above, so they move together.
		this.calculateQuantityBreaks();
	}

	// Admin, engineering or quality cost = hourly rate x hours. Only worked out when the rate or hours change,
	// so a cost entered before there were hours isn't wiped on load. Entering hours with no rate uses the default.
	calculateOneOffCosts(dataField: string) {
		const item = this.item as any;
		const cost = QAF_ONE_OFF_COSTS.find(c => c.rate === dataField || c.hours === dataField);
		if (!cost) return;
		if (dataField === cost.hours && !item[cost.rate]) {
			item[cost.rate] = (this.defaultSettings as any)?.[cost.rate] || 0;
		}
		item[cost.cost] = (item[cost.rate] || 0) * (item[cost.hours] || 0);
	}

	calculateOutsourcedProcessesLineData() {
		let outsourcedProcessesSubtotal = 0;
		let outsourcedMarginCost = 0;

		this.outsourcedProcesses.forEach((line) => {
			line.overhead = line.pricePerPart * ((line.marginPercentage || 0) / 100);
			outsourcedProcessesSubtotal += line.pricePerPart;
			outsourcedMarginCost += line.overhead;
		});

		this.item.outsourcedProcessesSubtotal = outsourcedProcessesSubtotal;
		this.item.outsourcedOverhead = outsourcedMarginCost;
		this.calculateFinancialSummary();
	}

	calculatePressProcessLineData() {
		this.item.pressProcessSubtotal = 0;

		this.pressProcesses.forEach((line) => {
			const process = this.processes.filter(x => x.id == line.processId)[0];
			const machine = this.machines.filter(x => x.id == line.machineId)[0];
			line.partsPerHour = Math.floor(3600 / line.processTime);
			line.tonnage = machine.tonnage;
			// Machine is the source of truth for the hourly machine rate; fall back to the legacy field on
			// Process for older data that hasn't had a rate set on the machine yet.
			const defaultMachineRate = (machine.hourlyMachineRate ?? process.hourlyMachineRate) || 0;
			if (line.hourlyRunningCostLabour === undefined || line.hourlyRunningCostLabour <= 0) {
				line.hourlyRunningCostLabour = line.defaultHourlyRunningCostLabour = process.hourlyLabourRate;
			}
			if (line.hourlyRunningCostMachine === undefined || line.hourlyRunningCostMachine <= 0) {
				line.hourlyRunningCostMachine = line.defaultHourlyRunningCostMachine = defaultMachineRate;
			}
			// A line with no stamp is new (loaded lines are stamped in stampAcceptedRates), so its default is
			// today's - any rate typed into it that differs is a change needing approval.
			if (line.defaultHourlyRunningCostLabour == null) {
				line.defaultHourlyRunningCostLabour = process.hourlyLabourRate;
			}
			if (line.defaultHourlyRunningCostMachine == null) {
				line.defaultHourlyRunningCostMachine = defaultMachineRate;
			}
			const men = line.men > 0 ? line.men : 1;
			line.hourlyRunningCostTotal = (line.hourlyRunningCostLabour * men) + line.hourlyRunningCostMachine;
			if (line.settingCost === undefined || line.settingCost <= 0) {
				// if the machine has an hourly setup cost defined use that, otherwise fall back to the default hourly cost.
				const hourlySettingCost = machine.setUpCost ?? defaultMachineRate;

				// take the hourly setup cost and convert to minutes multiplied by the setup time
				line.settingCost = hourlySettingCost * (line.setupTime || 0) / 60;
			}

			line.processCost = (line.hourlyRunningCostTotal / (line.partsPerHour * line.partsPerBlow)) + (line.settingCost / this.item.batchSize);
			this.item.pressProcessSubtotal += line.processCost;

			line.totalTime = line.setupTime + Math.ceil(line.processTime * this.item.batchSize / 60);
		});

		this.calculateFinancialSummary();
	}

	// The piece price at each break quantity, so one QAF can quote 5 / 10 / 25 / 1000 off. Each break runs
	// the real calculation again with its quantity standing in for the batch size, which is what makes the
	// price move: press setting cost, material in Fast quoting mode and every amortised tooling, fixture,
	// admin, engineering and quality cost are all divided by it. Restating that arithmetic here instead
	// would quietly drift from the Financial Summary; reusing the chain cannot.
	//
	// Children are quoted in step with the parent - an assembly at 25 off needs 25 x quantity per assembly
	// of each child, at every level down - so the tree is priced bottom up at each child's scaled quantity.
	// Everything runs against clones, so no saved QAF is touched and this stays a what-if.
	calculateQuantityBreaks() {
		// recalculatingChild points this.item at a child, and the breaks belong to the QAF on screen.
		if (this.calculatingQuantityBreaks === true || this.recalculatingChild === true || this.quantityBreaks.length === 0) {
			return;
		}

		this.calculatingQuantityBreaks = true;

		// Swapping the component's working line data for another QAF's is how recalculateChildQaf reuses
		// the calculation chain; the same swap prices a QAF at a quantity that is not its batch size.
		const priceFor = (qaf: any, quantity: number): number => {
			const children = (this.descendantQAFs || []).filter((d: any) => d.parentQuotationAnalysisFormId === qaf.id);
			const childTotal = children.reduce((total: number, child: any) => {
				const perAssembly = child.quantityPerAssembly || 1;
				return total + (priceFor(child, quantity * perAssembly) * perAssembly);
			}, 0);

			const saved = {
				boughtOutParts: this.boughtOutParts,
				childTotals: this.childTotals,
				item: this.item,
				outsourcedProcesses: this.outsourcedProcesses,
				pressProcesses: this.pressProcesses,
				rawMaterial: this.rawMaterial,
				toolingCosts: this.toolingCosts,
				toolingFixtures: this.toolingFixtures,
				weldAndAssembly: this.weldAndAssembly
			};

			try {
				const working: any = JSON.parse(JSON.stringify(qaf));
				working.batchSize = quantity;
				this.item = working;
				this.boughtOutParts = JSON.parse(working.boughtOutPartsLineData || "[]");
				this.outsourcedProcesses = JSON.parse(working.outsourcedProcessesLineData || "[]");
				this.pressProcesses = JSON.parse(working.pressProcessLineData || "[]");
				this.weldAndAssembly = JSON.parse(working.weldAndAssemblyLineData || "[]");
				this.toolingCosts = JSON.parse(working.toolingCostLineData || "[]");
				this.toolingFixtures = JSON.parse(working.toolingFixtureLineData || "[]");
				const rawMaterials = JSON.parse(working.rawMaterialLineData || "[]");
				this.rawMaterial = rawMaterials.length > 0 ? rawMaterials[0] : new RawMaterialLineData();
				// This QAF's own children, already priced at their scaled quantities.
				this.childTotals = { totalPartCost: childTotal };

				this.calculateBoughtOutMaterialsLineData();
				this.calculateRawMaterialData();
				this.calculateOutsourcedProcessesLineData();
				this.calculatePressProcessLineData();
				this.calculateWeldAndAssemblyLineData();
				this.calculateToolingCosts(); // cascades to fixtures + financial summary

				return working.totalPartCost || 0;
			}
			catch (e) {
				console.log("Quantity break calculation failed", e);
				return 0;
			}
			finally {
				this.boughtOutParts = saved.boughtOutParts;
				this.childTotals = saved.childTotals;
				this.item = saved.item;
				this.outsourcedProcesses = saved.outsourcedProcesses;
				this.pressProcesses = saved.pressProcesses;
				this.rawMaterial = saved.rawMaterial;
				this.toolingCosts = saved.toolingCosts;
				this.toolingFixtures = saved.toolingFixtures;
				this.weldAndAssembly = saved.weldAndAssembly;
			}
		};

		try {
			this.quantityBreaks.forEach((brk) => {
				const quantity = brk.quantity || 0;
				brk.piecePrice = quantity > 0 ? priceFor(this.item, quantity) : 0;
				brk.batchTotal = brk.piecePrice * quantity;
			});
		}
		finally {
			this.calculatingQuantityBreaks = false;
		}
	}

	calculateRawMaterialData() {
		const line = this.rawMaterial;
		if (!line) return;

		if (this.item.materialQuoting === MaterialQuoting.Fast) {
			const batchSize = this.item.batchSize > 0 ? this.item.batchSize : 1;
			const perPart = (line.pricePerPart || 0) > 0
				? line.pricePerPart!
				: (line.totalMaterialCost || 0) / batchSize;
			line.grossMaterialCost = perPart;
			line.overhead = perPart * ((line.marginPercentage || 0) / 100);
			// Margin% adds onto the net material cost.
			line.netMaterialCost = perPart + line.overhead;
		} else {
			if (!line.purchasableItemId) return;

			const material = this.materialPurchasableItems.filter(x => x.id == line.purchasableItemId)[0];
			if (!material) return;

			const weightConstant = material.weightConstant;
			line.materialGrossWeight = weightConstant * line.materialGauge * line.materialWidth * line.materialLength;
			line.partGrossWeight = weightConstant * line.materialGauge * line.partWidth * line.partPitch;

			if (line.partWidth > 0) {
				if (line.isCoil) {
					line.partsPerBlow = Math.floor(line.materialWidth / line.partWidth);
				} else {
					line.partsPerBlow = Math.floor((line.materialLength / line.partPitch) * (line.materialWidth / line.partWidth));
				}
			}

			if (line.isCoil) {
				line.partsPerMaterial = line.materialTonnage > 0 ? Math.floor(line.materialTonnage / (line.partGrossWeight * line.partsPerBlow)) : 0;
			} else {
				line.partsPerMaterial = Math.floor((line.materialLength * line.materialWidth) / (line.partPitch * line.partWidth));
			}

			// Scrap per part = the blank (part gross) weight less the finished net weight. Was using
			// materialGrossWeight (the whole sheet/coil), which made scrap weight nonsensically large.
			line.scrapWeight = line.netWeight === null || line.netWeight === undefined ? 0 : line.partGrossWeight - line.netWeight;
			line.scrapValue = line.scrapWeight * line.scrapCostPerKg;
			// Price is per tonne — divide by 1000 to get per-kg equivalent for calculation
			const pricePerKg = (line.rawMaterialPricePerKg || 0) / 1000;
			line.grossMaterialCost = pricePerKg * line.partGrossWeight;
			line.overhead = line.grossMaterialCost * ((line.marginPercentage || 0) / 100);
			// Margin% adds onto the net material cost.
			line.netMaterialCost = line.grossMaterialCost + line.overhead;
		}

		this.item.rawMaterialSubtotal = line.netMaterialCost;
		this.item.materialOverhead = line.overhead;
		this.item.rawMaterialMarginCost = line.overhead;

		this.calculateFinancialSummary();
	}

	calculateToolingCosts() {
		let totalToolingCosts = 0;

		this.toolingCosts.forEach((tool) => {
			if (tool.quoteType === "fast") {
				tool.toolingTotalCost = tool.fastQuoteTotalCost || 0;
			} else {
				tool.designCost = (tool.designRate || 0) * (tool.designHours || 0);
				tool.autoFormCost = (tool.autoFormRate || 0) * (tool.autoFormHours || 0);
				tool.machineCost = (tool.machineRate || 0) * (tool.machineHours || 0);
				tool.assemblyCost = (tool.assemblyRate || 0) * (tool.assemblyHours || 0);
				tool.qualityCost = (tool.qualityRate || 0) * (tool.qualityHours || 0);
				tool.toolingTotalCost = tool.designCost + tool.autoFormCost + (tool.toolingMaterialCost || 0) + (tool.accessoriesCost || 0) + tool.machineCost + tool.assemblyCost + tool.qualityCost;
			}
			tool.marginCost = tool.toolingTotalCost * ((tool.marginPercentage || 0) / 100);
			totalToolingCosts += tool.toolingTotalCost;
		});

		this.item.totalToolingCosts = totalToolingCosts;
		this.calculateToolingFixturesLineData();
		this.calculateFinancialSummary();
	}

	calculateToolingFixturesLineData() {
		let toolingFixtureSubtotal = 0;

		this.toolingFixtures.forEach((line) => {
			if (line.total !== undefined && line.total > 0) {
				toolingFixtureSubtotal += line.total;
			}
		});

		this.item.toolingFixtureSubtotal = toolingFixtureSubtotal;
		this.item.totalFixtureCosts = toolingFixtureSubtotal;
	}

	get toolingAmortisedClass(): string {
		if (!this.toolingCosts || this.toolingCosts.length === 0) return "";
		return this.toolingCosts.every((t) => t.amortised) ? "tooling-amortised-all" : "tooling-amortised-partial";
	}

	// The approver's name for the approval panel.
	get approvedByName(): string {
		const approver: any = (this.userProfiles || []).find((u: any) => u.id === this.item?.approvedById);
		return approver ? approver.fullName : "";
	}


	// Rates on screen that differ from the default they were filled from - the same check the API makes on
	// save, so the panel reflects edits before they're saved. Fast-quote tools don't use rates.
	get rateChanges(): { default: number; rate: string; value: number; where: string }[] {
		const out: { default: number; rate: string; value: number; where: string }[] = [];
		const round = (n: number) => Math.round((n || 0) * 100) / 100;
		const check = (where: string, rate: string, value?: number | null, def?: number | null) => {
			if (value != null && def != null && round(value) !== round(def)) {
				out.push({ default: def, rate, value, where });
			}
		};
		(this.toolingCosts || []).forEach((t, i) => {
			if (t.quoteType === "fast") {
				return;
			}
			const where = `Tool ${i + 1}: ${t.name || "Tool"}`;
			check(where, "Design rate", t.designRate, t.defaultDesignRate);
			check(where, "AutoForm rate", t.autoFormRate, t.defaultAutoFormRate);
			check(where, "Machine rate", t.machineRate, t.defaultMachineRate);
			check(where, "Assembly rate", t.assemblyRate, t.defaultAssemblyRate);
			check(where, "Quality rate", t.qualityRate, t.defaultQualityRate);
		});
		(this.pressProcesses || []).forEach((l, i) => {
			check(`Router line ${i + 1}`, "Hourly labour rate", l.hourlyRunningCostLabour, l.defaultHourlyRunningCostLabour);
			check(`Router line ${i + 1}`, "Hourly machine rate", l.hourlyRunningCostMachine, l.defaultHourlyRunningCostMachine);
		});
		return out;
	}

	get qualityAmortisedClass(): string {
		if (!this.item?.qualityLevelCost) return "";
		return this.item.qualityAmortised ? "tooling-amortised-all" : "tooling-amortised-partial";
	}

	get fixturesAmortisedClass(): string {
		if (!this.toolingFixtures || this.toolingFixtures.length === 0) return "";
		return this.toolingFixtures.every((f) => f.amortised) ? "tooling-amortised-all" : "tooling-amortised-partial";
	}

	calculateWeldAndAssemblyLineData() {
		this.item.weldAndAssemblySubtotal = 0;

		this.weldAndAssembly.forEach((line) => {
			line.hourlyRunningCostTotal = line.hourlyRunningCostLabour + line.hourlyRunningCostMachine;
			line.processCost = line.hourlyRunningCostTotal / line.partsPerHour;
			this.item.weldAndAssemblySubtotal += line.processCost;
		});

		this.calculateFinancialSummary();
	}

	cancelEditing() {
		if (confirm("Are you sure you wish to switch modes? Unsaved changes will be discarded.") == false) {
			return;
		}
		this.router.navigate([`/${this.returnUrl}/${this.item.id}`])
			.then(() => {
				window.location.reload();
			});
	}

	// Friendly names for the change log. Anything not listed is split from PascalCase.
	changeLogFieldLabel(field: string): string {
		const labels: { [field: string]: string } = {
			BoughtOutPartsDocumentsLineData: "BOM documents",
			BoughtOutPartsLineData: "Bought out parts",
			CADFiles: "CAD files",
			ClientContactId: "Contact",
			ClientId: "Client",
			CrmOpportunityId: "CRM opportunity",
			LinkedQuotationAnalysisFormIds: "Combined QAFs",
			OutsourcedProcessesDocumentsLineData: "Outsourced process documents",
			OutsourcedProcessesLineData: "Outsourced processes",
			ParentQuotationAnalysisFormId: "Parent QAF",
			PressProcessLineData: "Router",
			ProjectId: "Project",
			RawMaterialLineData: "Raw material",
			SupportingDocumentsLineData: "Supporting documents",
			ToolingCostLineData: "Tools",
			ToolingFixtureLineData: "Fixtures",
			TotalPartCost: "Piece price",
			WeldAndAssemblyLineData: "Weld & assembly",
		};
		return labels[field] || field.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
	}

	// Ids are stored as numbers; show the name where the page already has the list loaded.
	changeLogValue(field: string, value: string): string {
		if (value == null || value === "") {
			return "";
		}
		if (field === "LinkedQuotationAnalysisFormIds") {
			return value.replace(/line(s?)/, "QAF$1");
		}
		const id = Number(value);
		switch (field) {
			case "ClientId": return (this.clients.find(c => c.id === id) as any)?.name || value;
			case "ClientContactId": return (this.clientContacts.find(c => c.id === id) as any)?.displayName || value;
			case "CrmOpportunityId": return this.crmOpportunities.find((o: any) => o.id === id)?.title || value;
			case "ProjectId": return (this.projects.find((p: any) => p.id === id) as any)?.name || value;
			default: return value;
		}
	}

	checkForGeneratedPart() {
		// Single source of truth for both part-related buttons, driven by whether a Part actually exists
		// for this part number (rather than assuming one does because a part number is set).
		this.generatedPartId = null;
		this.headerTertiaryButtons = this.headerTertiaryButtons.filter(b => b.method !== "createSalesOrderPopup");
		if (!this.item.partNumber || this.item.partNumber.length < 2) {
			return;
		}
		this.partService.partExists(this.item.partNumber)
			.subscribe(
				(res) => {
					const exists = res.exists === true && !!res.partId;
					this.item.partNumberExists = exists;
					this.generatedPartId = exists ? res.partId : null;

					// Generate Sales Order = "the QAF is won". View mode + filled in is enough; the part
					// (and the quote's tools) are generated as part of that action if they don't exist yet,
					// so we no longer require a part to already exist here.
					if (this.mode === "view" && this.item.isFilledIn === true
						&& this.headerTertiaryButtons.some(b => b.method === "createSalesOrderPopup") === false) {
						this.headerTertiaryButtons.push({ icon: "fal fa-shopping-cart", method: "createSalesOrderPopup", text: "Generate Sales Order" });
					}
				},
				(err) => { console.log(err); }
			);
	}

	checkIfNeedsFillingIn() {
		this.item.isFilledIn = false;

		if (this.item.partNumber === null || this.item.partNumber === undefined || this.item.partNumber.length < 2) {
			return;
		}

		if (this.item.projectId === null || this.item.projectId === undefined || this.item.projectId < 1) {
			return;
		}

		if (this.item.clientId === null || this.item.clientId === undefined || this.item.clientId < 1) {
			return;
		}

		if (this.item.clientContactId === null || this.item.clientContactId === undefined || this.item.clientContactId < 1) {
			return;
		}

		if (this.item.partImageUrl === null || this.item.partImageUrl === undefined || this.item.partImageUrl.length < 2) {
			return;
		}

		if (this.item.partDescription === null || this.item.partDescription === undefined || this.item.partDescription.length < 2) {
			return;
		}

		if (this.item.annualVolume === null || this.item.annualVolume === undefined || this.item.annualVolume < 1) {
			return;
		}

		if (this.item.batchSize === null || this.item.batchSize === undefined || this.item.batchSize < 1) {
			return;
		}

		if (this.item.leadTimeFirstOff < 0) {
			return;
		}

		if (this.item.leadTimePhaseThree < 0) {
			return;
		}

		this.item.isFilledIn = true;
	}

	childFieldChanged(child: QuotationAnalysisForm) {
		// Live recompute so the child's read-only totals and the master's roll-up update as you edit.
		this.recalculateChildQaf(child);
	}

	clientPdfPopup() {
		const blocker = this.rateApprovalBlocker(true);
		if (blocker) {
			this.toastService.error(`${blocker} has changed rates awaiting Super Admin approval - the PDF can't be produced until they're approved.`);
			return;
		}
		this.popupClientPdf = { toolingView: "itemised" };
		this.popupTitle = "Client PDF";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.exportClientPdf(), text: "Generate PDF" };
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	closePopup() {
		this.popupVisible = false;
	}

	createItem() {
		if (this.isSaving) return;
		this.isSaving = true;
		this.prepareItemForSaving();
		this.qafService.createSingle(this.item)
			.subscribe(
				(res: any) => {
					// Angular reuses this component instance when navigating to the edit URL for the
					// same route, so the flag must be reset here or every later save silently no-ops.
					this.isSaving = false;
					this.toastService.success("Successfully Created " + this.itemType);
					this.router.navigate([`/${this.returnUrl}/${res.response.id}_edit`]);
				},
				(err) => {
					this.isSaving = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	// Typing a specification that isn't in the list adds it to the QAF's client, so the team no longer has
	// to leave the QAF and go into the client record. Create responds with Id 0 (the save runs after the
	// response is sent), so the client's list is reloaded and the new entry picked up from it. DevExtreme
	// only selects the resolved item - it doesn't add it to the list - hence the reassignment.
	createOutsourcedSpecification(e: any) {
		const text = (e.text || "").trim();
		const matches = (specs: OutsourcedSpecification[]) => specs.filter(s => (s.specification || "").trim().toLowerCase() === text.toLowerCase());

		if (text === "") {
			e.customItem = null;
			return;
		}

		const existing = matches(this.outsourcedSpecifications)[0];
		if (existing) {
			e.customItem = existing;
			return;
		}

		if (!this.item.clientId) {
			this.toastService.error("Select a client before adding a specification.");
			e.customItem = null;
			return;
		}

		e.customItem = new Promise((resolve) => {
			this.outsourcedSpecificationService.createSingle({ clientId: this.item.clientId, specification: text })
				.subscribe(
					() => {
						this.outsourcedSpecificationService.getByClientId(this.item.clientId)
							.subscribe(
								(res: any) => {
									const specs: OutsourcedSpecification[] = res.response;
									specs.sort((a, b) => a.specification.localeCompare(b.specification));
									this.outsourcedSpecifications = specs;
									const created = matches(specs).sort((a, b) => b.id - a.id)[0] || null;
									if (created) {
										this.toastService.success(`Specification "${text}" added`);
									}
									resolve(created);
								},
								(err) => {
									console.log(err);
									resolve(null);
								}
							);
					},
					(err) => {
						console.log(err);
						this.toastService.error("Could not add the specification.");
						resolve(null);
					}
				);
		});
	}

	createSalesOrder() {
		// Winning the QAF: make sure a Part exists (generate a draft from the quote if not), generate
		// the quote's tools, then raise the Sales Order — all off this single confirmation.
		if (!this.item.partNumber || this.item.partNumber.length < 2) {
			this.toastService.error("Please enter a part number first.");
			return;
		}
		if (!this.item.clientId || !this.item.clientContactId || !this.item.projectId) {
			this.toastService.error("A client, contact and project are required before winning this quote.");
			return;
		}
		this.checkIfNeedsFillingIn();
		if (this.item.isFilledIn !== true) {
			this.toastService.error("This QAF must be filled in before it can be won.");
			return;
		}
		if (confirm("This will create the parts for this QAF and its child QAFs (any that already exist are left as they are) and any tools on the quote, then create a Sales Order. Continue?") === false) {
			return;
		}

		this.ensurePartId().pipe(
			tap((partId) => { this.popupCreateSalesOrderFromPart.partId = partId; }),
			switchMap(() => this.generateQuoteTools())
		).subscribe(
			() => {
				this.salesOrderService.createFromPart(this.popupCreateSalesOrderFromPart)
					.subscribe(
						(res: any) => {
							this.popupVisible = false;
							this.popupCreateSalesOrderFromPart = new CreateSalesOrderFromPart();
							this.toastService.success("Parts, tools and sales order created.");
							this.router.navigate([`/admin/salesOrders/${res.response.lookupId}_edit`]);
						},
						(err: any) => {
							console.log(err);
							this.toastService.error("Could not create sales order. Please double check the details.");
						}
					);
			},
			(err) => {
				console.log(err);
				this.toastService.error(typeof err === "string" && err ? err : "Could not generate the parts or tools. Please try again.");
			}
		);
	}

	createSalesOrderPopup() {
		// Auto-fill the modal from the QAF costing so the user only reviews and adjusts before creating.
		// The Part (and tools) are generated on confirm, so a part need not exist to open this.
		const fixtureTotal = (desc: ToolingFixtureDescription) => this.toolingFixtures
			.filter((f) => f.description === desc)
			.reduce((sum, f) => sum + (f.total || 0), 0);

		this.popupCreateSalesOrderFromPart = new CreateSalesOrderFromPart();
		this.popupCreateSalesOrderFromPart.partId = this.generatedPartId || 0; // resolved/generated on confirm
		this.popupCreateSalesOrderFromPart.clientId = this.item.clientId || 0;
		this.popupCreateSalesOrderFromPart.clientContactId = this.item.clientContactId || 0;
		this.popupCreateSalesOrderFromPart.projectId = this.item.projectId || 0;
		this.popupCreateSalesOrderFromPart.quantity = 1;
		this.popupCreateSalesOrderFromPart.unitCost = this.item.totalPartCost || 0;
		this.popupCreateSalesOrderFromPart.deliveryCost = this.item.packagingAndLogistics || 0;
		this.popupCreateSalesOrderFromPart.toolingValue = this.item.totalToolingCosts || 0;
		this.popupCreateSalesOrderFromPart.weldFixtureValue = fixtureTotal(ToolingFixtureDescription.WeldFixture);
		this.popupCreateSalesOrderFromPart.laserFixtureValue = fixtureTotal(ToolingFixtureDescription.LaserFixture);
		this.popupCreateSalesOrderFromPart.inspectionFixtureValue = fixtureTotal(ToolingFixtureDescription.InspectionFixture) + fixtureTotal(ToolingFixtureDescription.CheckingFixture);
		this.popupCreateSalesOrderFromPart.assemblyFixtureValue = 0;

		this.popupTitle = "Create Sales Order";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.createSalesOrder(), text: "Create Sales Order" };
	}

	deleteCADFileLink(e: any) {
		const index = this.cadFiles.findIndex(d => d.lookupId === e.row.data.lookupId);
		this.cadFiles.splice(index, 1);

		return false;
	}

	deleteChild(child: QuotationAnalysisForm) {
		if (confirm("Remove this child QAF from the parent? The child QAF itself is not deleted.") === false) return;
		this.qafService.removeParentFromQAF(child.id)
			.subscribe(
				() => {
					this.toastService.success("Child removed");
					if (this.expandedChildId === child.id) this.expandedChildId = null;
					this.getChildQAFs();
				},
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	deleteSectionDocumentLink(e: any) {
		[this.boughtOutPartsDocuments, this.outsourcedProcessesDocuments].forEach((docs) => {
			const index = docs.findIndex(d => d.lookupId === e.row.data.lookupId);
			if (index > -1) {
				docs.splice(index, 1);
			}
		});

		return false;
	}

	deleteSupportingDocumentLink(e: any) {
		const index = this.supportingDocuments.findIndex(d => d.lookupId === e.row.data.lookupId);
		this.supportingDocuments.splice(index, 1);

		return false;
	}

	displayMachineInDropdown(e: any) {
		return e === null || e === undefined ? "" : (e.cellNumber === null || e.cellNumber === undefined ? "???" : e.cellNumber) + " - " + e.name;
	}

	displayQAFInDropdown(e: any) { 
		return e === null || e === undefined ? "" : e.id + " - " + e.partNumber;
	}

	downloadFileLink(e: any) {
		const lookupId = Guid.parse(e.row.data.lookupId);
		this.fileService.downloadFile(lookupId).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", e.row.data.fileName);
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
		return false;
	}

	// Full duplicate of an existing child QAF as a new sibling — every field and all line data (materials,
	// bought-out, processes, router, tooling, weld/assembly, files), unlike "Add Child (Same Details)" which
	// strips the router and tooling. Identity/approval are reset and the tile image is not carried over.
	duplicateChild(child: any) {
		const copy: any = JSON.parse(JSON.stringify(child));
		// Drop transient UI-only fields so only persisted data is sent.
		["parsedBoughtOutParts", "parsedOutsourcedProcesses", "parsedPressProcesses", "parsedRawMaterial", "parsedToolingCosts", "parsedToolingFixtures", "expanded"].forEach((k) => delete copy[k]);
		copy.id = 0;
		delete copy.lookupId;
		copy.parentQuotationAnalysisFormId = this.item.id;
		copy.approved = false;
		copy.approvedById = null;
		delete copy.approvedOn; // non-nullable DateTime on the API — send absent, not null, on create
		copy.partNumberExists = false;
		copy.partImageUrl = ""; // the tile image is not carried onto a child

		this.qafService.createSingle(copy)
			.subscribe(
				() => {
					this.toastService.success("Child QAF duplicated");
					this.getChildQAFs();
				},
				(err) => {
					console.log("Duplicate child QAF failed", err);
					this.toastService.error(err?.error?.error || err?.error?.message || err?.message || "Could not duplicate child QAF");
				}
			);
	}

	// Router grid: duplicate a single line as a new row so it can be tweaked rather than re-entered.
	duplicatePressProcessLine(e: any) {
		const copy = JSON.parse(JSON.stringify(e.row.data));
		this.pressProcesses = [...this.pressProcesses, copy];
		this.calculatePressProcessLineData();
	}
	// Full duplicate of the QAF currently open, as a new QAF at the same level (a child stays a child of the
	// same parent, a top-level QAF stays top-level). All line data is copied; identity/approval are reset.
	duplicateQaf() {
		if (!this.item.id) {
			this.toastService.error("Save this QAF before duplicating.");
			return;
		}
		this.prepareItemForSaving(); // sync the working arrays onto this.item first
		const copy: any = JSON.parse(JSON.stringify(this.item));
		copy.id = 0;
		delete copy.lookupId;
		copy.approved = false;
		copy.approvedById = null;
		delete copy.approvedOn; // non-nullable DateTime on the API — send absent, not null, on create
		copy.partNumberExists = false;

		this.qafService.createSingle(copy)
			.subscribe(
				(res: any) => {
					this.toastService.success("QAF duplicated");
					this.router.navigate([`${this.returnUrl}/${res.response.id}_edit`]).then(() => window.location.reload());
				},
				(err) => {
					console.log("Duplicate QAF failed", err);
					this.toastService.error(err?.error?.error || err?.error?.message || err?.message || "Could not duplicate QAF");
				}
			);
	}
	edit() {
		this.router.navigate([`/${this.returnUrl}/${this.item.id}_edit`])
			.then(() => {
				window.location.reload();
			});
	}

	// Resolve the Part id for this QAF. The API creates a draft Part for this QAF and each child QAF below it
	// (filled from the quote: material, gauge, blank size, router, bought out parts...), leaving any part number
	// that's already a Part untouched, and returns this QAF's part.
	ensurePartId(): Observable<number> {
		return this.qafService.generateParts(this.item.id).pipe(
			map((res: any) => {
				const result = res.response || res;
				const created = (result.parts || []).filter((p: any) => p.created).map((p: any) => p.partNumber);
				if (created.length > 0) {
					this.toastService.info(`Created ${created.length === 1 ? "part" : "parts"} ${created.join(", ")}`);
				}
				return result.partId || 0;
			}),
			tap((partId) => {
				this.generatedPartId = partId || null;
				this.item.partNumberExists = !!partId;
			})
		);
	}

	exportClientPdf() {
		const html = this.buildClientPdfHtml(this.popupClientPdf.toolingView);
		const win = window.open("", "_blank");
		if (win) {
			win.document.open();
			win.document.write(html);
			win.document.close(); // triggers the body onload -> window.print()
		}
		this.closePopup();
	}

	// Sets any admin, engineering or quality rate that's still empty to its default from the QAF settings.
	fillOneOffRates() {
		const item = this.item as any;
		QAF_ONE_OFF_COSTS.forEach((c) => {
			if (!item[c.rate]) {
				item[c.rate] = (this.defaultSettings as any)?.[c.rate] || 0;
			}
		});
	}

	// The combined-quote picker: anything else quoted for this client in the same currency - a child QAF of
	// another quote included, since a quote can be combined with one. This QAF's own family is left out: its
	// parent, and its children, already come out on the PDF with it, so combining one would charge it twice.
	filterLinkableQAFs() {
		const family = new Set<number>([this.item.id, ...(this.descendantQAFs || []).map(q => q.id)]);
		if (this.item.parentQuotationAnalysisFormId) {
			family.add(this.item.parentQuotationAnalysisFormId);
		}

		this.filteredLinkableQAFs = (this.clientQAFs || [])
			.filter(q => q.currency === this.item.currency && family.has(q.id) === false);
	}

	filterParentQAFs() {
		this.filteredParentQAFs = [];

		if (this.item.clientId != undefined) {
			this.filteredParentQAFs = this.parentQAFs.filter(q => q.clientId === this.item.clientId && q.id !== this.item.id);
		}

		this.filterLinkableQAFs();

		// The available-parents list only contains top-level QAFs, so a QAF whose linked parent is itself
		// a child (or hasn't loaded yet) would show an empty dropdown. Ensure the currently-linked parent
		// is always present so the selection is displayed.
		const parentId = this.item.parentQuotationAnalysisFormId;
		if (parentId && this.filteredParentQAFs.some(q => q.id === parentId) === false) {
			const known = this.parentQAFs.find(q => q.id === parentId);
			if (known) {
				this.filteredParentQAFs = [known, ...this.filteredParentQAFs];
			} else {
				this.qafService.getSingleById(parentId).subscribe((res: any) => {
					if (res && res.response) {
						this.parentQAFs = [res.response, ...this.parentQAFs]; // cache so we re-add without re-fetching
						this.filterParentQAFs();
					}
				});
			}
		}
	}

	// Parts per hour is always shown rounded down, regardless of any decimal value stored on the line.
	floorPartsPerHourText(cellInfo: any) {
		return cellInfo.value == null ? "" : Math.floor(cellInfo.value).toString();
	}

	generateChildQaf() {
		if (!this.item.id) {
			this.toastService.error("Save this QAF before adding a child.");
			return;
		}
		// Create a new child QAF that copies this QAF's full details (a new part linked to this one),
		// in place — the new child appears in the inline list below without leaving the master.
		this.prepareItemForSaving(); // sync line-data from the working arrays onto this.item first
		const child: any = JSON.parse(JSON.stringify(this.item));
		// Router and tooling are not carried over to a same-details child; clear them, then recompute so
		// the child's piece price / totals reflect the removal before it is created.
		child.pressProcessLineData = "[]";
		child.toolingCostLineData = "[]";
		child.toolingFixtureLineData = "[]";
		this.recalculateChildQaf(child);
		child.id = 0;
		delete child.lookupId;
		child.parentQuotationAnalysisFormId = this.item.id;
		child.approved = false;
		child.approvedById = null;
		delete child.approvedOn; // non-nullable DateTime on the API — send absent, not null, on create
		child.partNumberExists = false;
		child.partImageUrl = ""; // the tile image is not carried onto a child
		this.qafService.createSingle(child)
			.subscribe(
				() => {
					this.toastService.success("Child QAF created with the same details");
					this.getChildQAFs();
				},
				(err) => {
					console.log("Create child QAF failed", err);
					this.toastService.error(err?.error?.error || err?.error?.message || err?.message || "Could not create child QAF");
				}
			);
	}

	generatePreviewPDF() {
		const blocker = this.rateApprovalBlocker(false);
		if (blocker) {
			this.toastService.error(`${blocker} has changed rates awaiting Super Admin approval - the PDF can't be produced until they're approved.`);
			return;
		}
		this.qafService.generateAndPreviewPdf(this.popupGenerateAndDownloadQuotationAnalysisFormPDFDto).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "Preview_QAF_" + this.item.id + ".pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
				this.closePopup();
			}
		);
	}

	generatePreviewPDFPopup() {
		// Generate the parent-only PDF directly — no popup, children are not included.
		this.popupGenerateAndDownloadQuotationAnalysisFormPDFDto = new GenerateAndDownloadQuotationAnalysisFormPDFDto();
		this.popupGenerateAndDownloadQuotationAnalysisFormPDFDto.quotationAnalysisFormId = this.item.id;
		this.popupGenerateAndDownloadQuotationAnalysisFormPDFDto.includeChildren = false;
		this.generatePreviewPDF();
	}

	// Create one draft ProductionTool per tooling line on the quote. Tools default to the Redditch
	// toolroom + first active tooling type (the QAF doesn't carry these) and are completed later on
	// the Tool page. Existing tools (e.g. from a previous win) fail the unique-name rule and are
	// skipped, so re-winning never duplicates.
	generateQuoteTools(): Observable<any> {
		const lines = this.toolingCosts || [];
		if (lines.length === 0) {
			return of([]);
		}
		const toolingTypeId = this.toolingTypes.length > 0 ? this.toolingTypes[0].id : 0;
		if (toolingTypeId === 0) {
			this.toastService.info("No active tooling types set up, so tools were not generated.");
			return of([]);
		}
		const toolroomSiteId = 5; // Redditch toolroom — tools are made here by default
		const usedNames = new Set<string>();
		const partId = this.generatedPartId || 0;

		// Sequential so the server-side TL-<partNumber> suffixing (-2, -3…) is deterministic when a part
		// gets more than one tool.
		return from(lines).pipe(
			concatMap((line, index) => {
				let name = (line.name || "Tool").trim();
				// Disambiguate duplicate names within the same quote so each line still creates a tool.
				if (usedNames.has(name)) {
					name = `${name} (Stage ${line.stageNumber || index + 1})`;
				}
				usedNames.add(name);

				const tool = new ProductionTool({
					clientId: this.item.clientId!,
					currentServicePeriodBlows: 0,
					expectedLifetimeBlows: 0,
					imageURLs: "[]",
					isStackable: false,
					lifetimeBlows: 0,
					name,
					notes: `Generated from QAF #${this.item.id}`,
					ownership: ToolOwnershipEnum.BPL,
					poNumber: "",
					poURL: "",
					projectId: this.item.projectId || undefined,
					rackLocation: "",
					serviceIntervalBlows: 0,
					siteId: toolroomSiteId,
					toolHeight: 0,
					toolLength: 0,
					toolWeightLower: 0,
					toolWeightUpper: 0,
					toolWidth: 0,
					toolingTypeId,
					totalStorageVolume: 0,
					totalToolWeight: 0
				});

				return this.productionToolService.createSingle(tool).pipe(
					switchMap((res: any) => {
						const created = res && res.response;
						// Link the tool to the QAF's part (router stage from the tooling line). The server then
						// auto-names the tool from the part. Skip if the part isn't resolved yet.
						if (!created || !created.id || !partId) return of(created);
						const link = new ProductionToolPart({
							isActive: true,
							partId,
							productionToolId: created.id,
							routerStage: line.stageNumber || index + 1
						});
						return this.productionToolPartService.createSingle(link).pipe(
							map(() => created),
							catchError(() => of(created))
						);
					}),
					catchError(() => of(null))
				);
			}),
			toArray()
		);
	}

	generateTool(tool: ToolingCostLineData) {
		if (!tool.name || tool.name.length < 2) {
			this.toastService.error("Please enter a tool name first.");
			return;
		}
		if (!this.item.clientId) {
			this.toastService.error("Please select a client first.");
			return;
		}
		const queryParams: any = { clientId: this.item.clientId, name: tool.name };
		if (this.item.projectId) queryParams.projectId = this.item.projectId;
		const urlTree = this.router.createUrlTree(["/" + environment.build.adminRoute + "/productionTools/create"], { queryParams });
		window.open(this.router.serializeUrl(urlTree), "_blank");
	}

	getChangeLog() {
		this.qafService.getChangeLog(this.item.id)
			.subscribe(
				(res: any) => {
					this.changeLog = (res.response || []).map((c: any) => ({
						...c,
						fieldLabel: this.changeLogFieldLabel(c.field),
						newValue: this.changeLogValue(c.field, c.newValue),
						oldValue: this.changeLogValue(c.field, c.oldValue),
					}));
				},
				(err) => console.log(err)
			);
	}

	getChildQAFs() {
		this.qafService.getByParentId(this.item.id)
			.subscribe(
				(res: any) =>  {
					this.childQAFs = res.response;
					this.getDescendantQAFs();
				},
				(err) => console.log(err)
			);
	}

	getClientContacts()	{
		this.clientContactService.getContactByParentId(this.item.clientId)
			.subscribe(
				(res: any) => {
					this.clientContacts = res.response;
					if (this.clientContacts.length === 1) {
						this.item.clientContactId = this.clientContacts[0].id;
					} else {
						this.clientContacts.sort((a,b) => a.displayName.localeCompare(b.displayName));
					}
				},
				(err) => console.log(err)
			);
	}

	// Everything quoted for this client, for the combined-quote picker (child QAFs included).
	getClientQAFs() {
		if (!this.item.clientId) {
			this.clientQAFs = [];
			this.filterLinkableQAFs();
			return;
		}
		this.qafService.getForClient(this.item.clientId)
			.subscribe(
				(res: any) => {
					this.clientQAFs = (res.response || []).filter((q: any) => q.id !== this.item.id);
					this.filterLinkableQAFs();
				},
				(err) => console.log(err)
			);
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) =>  {
					this.clients = res.response.items;
					this.clients.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	// CRM opportunities for the QAF's client (sensitive ones only for their creator and Super Admins).
	getCrmOpportunities() {
		if (!this.item.clientId) {
			this.crmOpportunities = [];
			return;
		}
		this.crmOpportunityService.getByClientId(this.item.clientId)
			.subscribe(
				(res: any) => {
					this.crmOpportunities = res.response || [];
				},
				(err) => console.log(err)
			);
	}

	getDefaultSettings() {
		this.qafService.defaultSettings()
			.subscribe(
				(res: any) => {
					this.defaultSettings = res.response;
					// A new QAF starts with the default admin, engineering and quality rates.
					if (!this.item.id) {
						this.fillOneOffRates();
					}
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getDescendantQAFs() {
		this.qafService.getDescendantsById(this.item.id)
			.subscribe(
				(res: any) => {
					this.descendantQAFs = res.response || [];
					this.updateChildTotals();
				},
				(err) => {
					// Fall back to the direct children so the totals still add up to something sensible
					// rather than dropping to zero if the subtree call fails.
					console.log(err);
					this.descendantQAFs = this.childQAFs || [];
					this.updateChildTotals();
				}
			);
	}

	getItem(itemId: number, mode: string) {
		this.qafService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.lockCalculations = true;
					this.item = res.response;
					// Set from the loaded item so the child banner isn't affected by recalculateChildQaf
					// temporarily pointing this.item at a child during inline edits.
					this.isChildQaf = (res.response.parentQuotationAnalysisFormId || 0) > 0;
					this.boughtOutParts = JSON.parse(this.item.boughtOutPartsLineData || "[]");
					this.cadFiles = JSON.parse(this.item.cadFiles || "[]");
					this.boughtOutPartsDocuments = JSON.parse(this.item.boughtOutPartsDocumentsLineData || "[]");
					this.outsourcedProcessesDocuments = JSON.parse(this.item.outsourcedProcessesDocumentsLineData || "[]");
					this.outsourcedProcesses = JSON.parse(this.item.outsourcedProcessesLineData || "[]");
					this.pressProcesses = JSON.parse(this.item.pressProcessLineData || "[]");
					this.quantityBreaks = JSON.parse(this.item.quantityBreakLineData || "[]");
					this.supportingDocuments = JSON.parse(this.item.supportingDocumentsLineData || "[]");
					this.toolingFixtures = JSON.parse(this.item.toolingFixtureLineData || "[]");
					this.weldAndAssembly = JSON.parse(this.item.weldAndAssemblyLineData || "[]");

					// Raw material — single object
					const rawMaterials = JSON.parse(this.item.rawMaterialLineData || "[]");
					this.rawMaterial = rawMaterials.length > 0 ? rawMaterials[0] : new RawMaterialLineData();

					// Modular tooling
					this.toolingCosts = JSON.parse(this.item.toolingCostLineData || "[]");
					this.stampAcceptedRates();
					this.linkedQafIds = JSON.parse(this.item.linkedQuotationAnalysisFormIds || "[]");
					this.loadLinkedQuotes();

					// Whether a Part actually exists is resolved by checkForGeneratedPart() below via the
					// real Part/PartExists lookup — never assumed from the part number being present.

					this.setImageStyle(this.item.partImageUrl);
					this.getChildQAFs();
					this.getChangeLog();
					this.getClientContacts();
					this.getClientQAFs();
					this.getCrmOpportunities();
					this.getOutsourcedSpecifications();
					this.getProjects();
					this.filterParentQAFs();
					this.buildButtons(mode);
					this.checkForGeneratedPart();
					// Recompute derived totals from the loaded line data so read-only summary figures
					// (material / bought-out / tooling margins, piece price, etc.) reflect current values
					// rather than whatever was persisted under older calculation logic.
					this.calculateRawMaterialData();
					this.calculateBoughtOutMaterialsLineData();
					this.calculateToolingCosts();
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");},
				() => { this.pageLoadedAt = new Date(); }
			);
	}

	getMachines() {
		this.machineService.getAll()
			.subscribe(
				(res: any) =>  {
					this.machines = res.response.items;
					this.machines.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getOutsourcedSpecifications()	{
		this.outsourcedSpecificationService.getByClientId(this.item.clientId)
			.subscribe(
				(res: any) => {
					this.outsourcedSpecifications = res.response;
					this.outsourcedSpecifications.sort((a,b) => a.specification.localeCompare(b.specification));
				},
				(err) => console.log(err)
			);
	}

	getPackaging() {
		this.packagingService.getAll()
			.subscribe(
				(res: any) =>  {
					this.packaging = res.response.items;
					this.packaging.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getParentQAFs() {
		this.qafService.getWithNoParent()
			.subscribe(
				(res: any) =>  {
					this.parentQAFs = res.response;
					this.filterParentQAFs();
				},
				(err) => console.log(err)
			);
	}

	getPaymentTerms() {
		this.paymentTermsService.getAll()
			.subscribe(
				(res: any) => {
					this.paymentTerms = res.response.items;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getProcesses() {
		this.processService.getAll()
			.subscribe(
				(res: any) =>  {
					this.processes = res.response.items;
					this.processes.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getProjects() {
		this.projectService.getProjectsByClient(this.item.clientId)
			.subscribe(
				(res: any) =>  {
					this.projects = res.response;
					this.projects.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getPurchasableItems() {
		this.purchasableItemService.getAll()
			.subscribe(
				(res: any) => {
					this.purchasableItems = res.response.items;
					this.purchasableItems.sort((a,b) => a.name.localeCompare(b.name));

					this.materialPurchasableItems = this.purchasableItems.filter((pi) => pi.purchaseType === PurchaseTypeEnum.Material);
				},
				(err) => console.log(err)
			);
	}

	getSuppliers() {
		this.supplierService.getAll()
			.subscribe(
				(res: any) =>  {
					this.suppliers = res.response.items;
					this.suppliers.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	// Every QAF whose tooling belongs to this assembly: the parent's own subtree, flattened.
	// descendantQAFs is loaded in one call, but the direct children are also edited in place by the
	// inline child grid (childFieldChanged mutates those instances), so the live childQAFs objects
	// win over the subtree's separately-loaded copies of the same rows - otherwise the tooling total
	// would ignore edits until the next refresh.
	getToolingScopeQAFs(): any[] {
		const byId = new Map<number, any>();
		(this.descendantQAFs || []).forEach((d: any) => byId.set(d.id, d));
		(this.childQAFs || []).forEach((c: any) => byId.set(c.id, c));
		return Array.from(byId.values());
	}

	getToolingTypes() {
		this.toolingTypeService.active()
			.subscribe(
				(res: any) => {
					this.toolingTypes = res.response;
				},
				(err) => console.log(err)
			);
	}

	getUserProfiles() {
		this.userProfileService.getActiveUsers()
			.subscribe(
				(res: any) =>  {
					this.userProfiles = res.response;
				},
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	// A new router line starts with a null stamp: calculatePressProcessLineData sets it to today's default,
	// so a rate typed into the new line that differs from it counts as a change.
	initNewPressProcessLine(e: any) {
		e.data.defaultHourlyRunningCostLabour = null;
		e.data.defaultHourlyRunningCostMachine = null;
	}

	inspectCADFile(e: any) {
		if (!this.cadViewer) return;
		this.cadViewer.screenshotName = `${this.item.partNumber || "qaf"}-3d`;
		this.cadViewer.open(e.row.data.lookupId);
		return false;
	}

	isCadFileInspectable = (e: any): boolean => {
		const name: string = e?.row?.data?.fileName || "";
		return /\.(stp|step)$/i.test(name);
	}

	linkedQafsChanged(e: any) {
		this.linkedQafIds = e.value || [];
		this.loadLinkedQuotes();
	}

	// Each linked QAF and its own subtree, so its tooling and one-off costs appear on the combined quote.
	loadLinkedQuotes() {
		const ids = (this.linkedQafIds || []).filter(id => id !== this.item.id);
		if (ids.length === 0) {
			this.linkedQuotes = [];
			return;
		}
		forkJoin(ids.map(id => forkJoin({ descendants: this.qafService.getDescendantsById(id), qaf: this.qafService.getSingleById(id) })))
			.subscribe(
				(results: any[]) => {
					this.linkedQuotes = results.map(r => ({ descendants: r.descendants.response || [], qaf: r.qaf.response }));
				},
				(err) => {
					console.log(err);
					this.toastService.error("Could not load the combined QAFs.");
				}
			);
	}

	mainFormDataChanged(args: any) {
		if (this.lockCalculations === true) {
			const currentTime = new Date;
			const time = (currentTime.getTime() - this.pageLoadedAt.getTime()) / 1000;

			if (time < 2) {
				return;
			}

			this.lockCalculations = false;
		}
		switch (args.dataField) {
			case "annualVolume":
			case "batchSize":
			case "projectLengthYears":
			case "packagingAndLogistics":
			case "containerCost":
			case "groupMarginPercentage":
			case "groupMarginAmount":
				if (args.dataField === "batchSize") {
					this.calculatePressProcessLineData();
					this.calculateRawMaterialData();
				}
				this.calculateFinancialSummary();
				break;
			case "clientId":
				{
					if (args.value !== undefined && args.value !== null) {
						this.getClientContacts();
						this.getClientQAFs();
						this.getCrmOpportunities();
						this.getOutsourcedSpecifications();
						this.getProjects();
						this.filterParentQAFs();
					}
				}
				break;
			case "currency":
				if (args.value === Currency.EUR) {
					this.currency = "EUR";
				} else {
					this.currency = "GBP";
				}
				// One quote, one currency - what can be combined changes with it.
				this.filterLinkableQAFs();
				break;
			case "adminRate":
			case "adminHours":
			case "engineeringRate":
			case "engineeringHours":
			case "qualityCostRate":
			case "qualityHours":
				this.calculateOneOffCosts(args.dataField);
				this.calculateFinancialSummary();
				break;
			case "processMarginPercentage":
			case "profitMarginPercentage":
			case "qualityLevelCost":
			case "adminAmortised":
			case "engineeringAmortised":
			case "qualityAmortised":
				this.calculateFinancialSummary();
				break;
			case "materialQuoting":
				this.calculateRawMaterialData();
				break;
		}

		this.checkIfNeedsFillingIn();
	}

	onInitNewPressProcessRow(e: any) {
		e.data.partsPerBlow = 1;
	}



	onInitNewToolingFixtureRow(e: any) {
		e.data.superseded = false;
	}

	openChild(child: QuotationAnalysisForm) {
		// Reload after navigating: parent<->child is a soft route change (same :id route reuses this
		// component), and the tabbed dx-form's grids don't reliably re-render when their arrays are swapped
		// underneath them. A full rebuild — as edit()/cancelEditing() already do — keeps the grids alive.
		this.router.navigate([`${this.returnUrl}/${child.id}`]).then(() => window.location.reload());
	}

	// Unpacks a child's stored line-data JSON into arrays the inline detail grids bind to. Cached onto
	// the child so the array references stay stable across change detection and the grids don't thrash.
	parseChildLineData(child: any) {
		try {
			child.parsedBoughtOutParts = JSON.parse(child.boughtOutPartsLineData || "[]");
			child.parsedOutsourcedProcesses = JSON.parse(child.outsourcedProcessesLineData || "[]");
			child.parsedPressProcesses = JSON.parse(child.pressProcessLineData || "[]");
			child.parsedToolingCosts = JSON.parse(child.toolingCostLineData || "[]");
			child.parsedToolingFixtures = JSON.parse(child.toolingFixtureLineData || "[]");
			const rawMaterials = JSON.parse(child.rawMaterialLineData || "[]");
			child.parsedRawMaterial = rawMaterials.length > 0 ? rawMaterials[0] : new RawMaterialLineData();
		} catch (e) {
			console.log("Could not parse child line data", e);
			child.parsedBoughtOutParts = [];
			child.parsedOutsourcedProcesses = [];
			child.parsedPressProcesses = [];
			child.parsedToolingCosts = [];
			child.parsedToolingFixtures = [];
			child.parsedRawMaterial = new RawMaterialLineData();
		}
	}

	// A one-off cost spread over the batch: cost / batch size.
	perPiece(cost: number): number {
		return this.item?.batchSize > 0 ? (cost || 0) / this.item.batchSize : 0;
	}

	popupPurchasableItemFormDataChanged(args: any) {
		// Material type only applies to material / tooling billet purchases; clear it otherwise.
		if (args.dataField === "purchaseType"
			&& this.popupPurchasableItem.purchaseType !== PurchaseTypeEnum.Material
			&& this.popupPurchasableItem.purchaseType !== PurchaseTypeEnum.ToolingBillet) {
			this.popupPurchasableItem.materialType = MaterialType.NotApplicable;
		}
		// Density (weightConstant) is derived from the material type.
		if (args.dataField === "materialType" || args.dataField === "purchaseType") {
			this.popupPurchasableItem.weightConstant = densityForMaterialType(this.popupPurchasableItem.materialType);
		}
	}

	prepareItemForSaving() {
		// Worked out again first, so the prices stored against each quantity agree with everything else
		// being saved rather than with whatever the figures were when a quantity was last typed in.
		this.calculateQuantityBreaks();
		this.item.quantityBreakLineData = JSON.stringify(this.quantityBreaks);
		this.item.boughtOutPartsLineData = JSON.stringify(this.boughtOutParts);
		this.item.linkedQuotationAnalysisFormIds = JSON.stringify(this.linkedQafIds || []);
		this.item.boughtOutPartsDocumentsLineData = JSON.stringify(this.boughtOutPartsDocuments);
		this.item.outsourcedProcessesDocumentsLineData = JSON.stringify(this.outsourcedProcessesDocuments);
		this.item.cadFiles = JSON.stringify(this.cadFiles);
		this.item.outsourcedProcessesLineData = JSON.stringify(this.outsourcedProcesses);
		this.item.pressProcessLineData = JSON.stringify(this.pressProcesses);
		this.item.rawMaterialLineData = JSON.stringify([this.rawMaterial]);
		this.item.supportingDocumentsLineData = JSON.stringify(this.supportingDocuments);
		this.item.toolingCostLineData = JSON.stringify(this.toolingCosts.map(t => { const { ...rest } = t; return rest; }));
		this.item.toolingFixtureLineData = JSON.stringify(this.toolingFixtures);
		this.item.weldAndAssemblyLineData = JSON.stringify(this.weldAndAssembly);

		this.checkIfNeedsFillingIn();
	}

	// The first QAF in the quote with changed rates awaiting approval - this one, its children (their piece
	// prices roll into it) or a combined QAF. The PDF can't be produced while there is one.
	rateApprovalBlocker(includeLinked: boolean): string | null {
		const linked = includeLinked ? this.linkedQuotes.reduce((all: any[], l) => [...all, l.qaf, ...l.descendants], []) : [];
		const blocker = [this.item, ...(this.descendantQAFs || []), ...linked].find((q: any) => q && q.rateApprovalRequired && !q.approved);
		return blocker ? (blocker.partNumber || "QAF #" + blocker.id) : null;
	}

	rawMaterialFormDataChanged() {
		if (this.lockCalculations === true) {
			const currentTime = new Date;
			const time = (currentTime.getTime() - this.pageLoadedAt.getTime()) / 1000;
			if (time < 2) return;
			this.lockCalculations = false;
		}
		this.calculateRawMaterialData();
		this.checkIfNeedsFillingIn();
	}

	recalculateChildQaf(child: QuotationAnalysisForm) {
		// Reuse the master calculation chain on a child by temporarily pointing the component's working
		// line-data at the child, recomputing, then restoring. Reference data (materials, machines,
		// processes) is shared between parent and child so it doesn't need swapping.
		const saved = {
			boughtOutParts: this.boughtOutParts,
			item: this.item,
			outsourcedProcesses: this.outsourcedProcesses,
			pressProcesses: this.pressProcesses,
			rawMaterial: this.rawMaterial,
			toolingCosts: this.toolingCosts,
			toolingFixtures: this.toolingFixtures,
			weldAndAssembly: this.weldAndAssembly,
		};
		try {
			this.recalculatingChild = true; // a child's own piece price must not include the parent's child roll-up
			this.item = child;
			this.boughtOutParts = JSON.parse(child.boughtOutPartsLineData || "[]");
			this.outsourcedProcesses = JSON.parse(child.outsourcedProcessesLineData || "[]");
			this.pressProcesses = JSON.parse(child.pressProcessLineData || "[]");
			this.weldAndAssembly = JSON.parse(child.weldAndAssemblyLineData || "[]");
			this.toolingCosts = JSON.parse(child.toolingCostLineData || "[]");
			this.stampAcceptedRates();
			this.toolingFixtures = JSON.parse(child.toolingFixtureLineData || "[]");
			const rawMaterials = JSON.parse(child.rawMaterialLineData || "[]");
			this.rawMaterial = rawMaterials.length > 0 ? rawMaterials[0] : new RawMaterialLineData();

			this.calculateBoughtOutMaterialsLineData();
			this.calculateRawMaterialData();
			this.calculateOutsourcedProcessesLineData();
			this.calculatePressProcessLineData();
			this.calculateWeldAndAssemblyLineData();
			this.calculateToolingCosts(); // cascades to fixtures + financial summary

			// Persist recomputed line data back onto the child so a save sends the fresh values.
			child.boughtOutPartsLineData = JSON.stringify(this.boughtOutParts);
			child.outsourcedProcessesLineData = JSON.stringify(this.outsourcedProcesses);
			child.pressProcessLineData = JSON.stringify(this.pressProcesses);
			child.weldAndAssemblyLineData = JSON.stringify(this.weldAndAssembly);
			child.toolingCostLineData = JSON.stringify(this.toolingCosts.map(t => { const { ...rest } = t; return rest; }));
			child.toolingFixtureLineData = JSON.stringify(this.toolingFixtures);
			child.rawMaterialLineData = JSON.stringify([this.rawMaterial]);
		} catch (e) {
			console.log("Child recalculation failed", e);
		} finally {
			this.item = saved.item;
			this.rawMaterial = saved.rawMaterial;
			this.boughtOutParts = saved.boughtOutParts;
			this.outsourcedProcesses = saved.outsourcedProcesses;
			this.pressProcesses = saved.pressProcesses;
			this.weldAndAssembly = saved.weldAndAssembly;
			this.toolingCosts = saved.toolingCosts;
			this.toolingFixtures = saved.toolingFixtures;
			this.recalculatingChild = false;
		}
		this.parseChildLineData(child); // refresh the inline detail grids with the recalculated costs
		this.updateChildTotals();
	}

	removeChildQAF(e: any) {
		this.qafService.removeParentFromQAF(e.row.data.id)
			.subscribe(
				() => {
					this.toastService.success("Successfully Removed Child");
					this.getChildQAFs();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
		this.router.navigate([`${this.returnUrl}/${e.row.data.id}`]);
	}

	removeTool(index: number) {
		if (confirm("Are you sure you wish to remove this tool?")) {
			this.toolingCosts.splice(index, 1);
			this.calculateToolingCosts();
		}
	}

	resetToolRate(tool: ToolingCostLineData, field: string) {
		switch (field) {
			case "design": tool.designRate = tool.defaultDesignRate = this.defaultSettings.designRate; break;
			case "autoForm": tool.autoFormRate = tool.defaultAutoFormRate = this.defaultSettings.autoFormRate; break;
			case "machine": tool.machineRate = tool.defaultMachineRate = this.defaultSettings.machineRate; break;
			case "assembly": tool.assemblyRate = tool.defaultAssemblyRate = this.defaultSettings.assemblyRate; break;
			case "quality": tool.qualityRate = tool.defaultQualityRate = this.defaultSettings.qualityRate; break;
		}
		this.calculateToolingCosts();
		return false;
	}

	returnToParent() {
		if (this.item.parentQuotationAnalysisFormId) {
			this.router.navigate([`${this.returnUrl}/${this.item.parentQuotationAnalysisFormId}`]).then(() => window.location.reload());
		}
	}

	saveAndSwitchToView() {
		// Used after generating a part: persist the QAF and drop into view mode, where the
		// Generate Sales Order action becomes available.
		if (this.isSaving) return;
		this.isSaving = true;
		this.prepareItemForSaving();
		const goToView = (id: number) => {
			this.isSaving = false;
			this.router.navigate([`/${this.returnUrl}/${id}`]).then(() => { window.location.reload(); });
		};
		const failed = (err: any) => {
			this.isSaving = false;
			console.log(err);
			this.toastService.error("Part generated, but the QAF could not be saved.");
		};
		if (this.item.id) {
			this.qafService.updateSingleById(this.item.id, this.item).subscribe(() => goToView(this.item.id), failed);
		} else {
			this.qafService.createSingle(this.item).subscribe((res: any) => goToView(res.response.id), failed);
		}
	}

	saveChild(child: QuotationAnalysisForm) {
		this.recalculateChildQaf(child);
		this.qafService.updateSingleById(child.id, child)
			.subscribe(
				() => { this.toastService.success("Child QAF saved"); this.getChildQAFs(); },
				(err) => { console.log(err); this.toastService.error("Could not save child QAF"); }
			);
	}

	sectionDocuments(section: "boughtOut" | "outsourced"): SupportingDocumentFileLineData[] {
		return section === "boughtOut" ? this.boughtOutPartsDocuments : this.outsourcedProcessesDocuments;
	}

	setImageStyle(imageUrl: string) {
		this.imageStyle = `background-image: url("${imageUrl}")`;
	}

	// Rates saved before rate approval existed carry no record of their default. Take them as they stand -
	// they were accepted when priced - so only changes made from here on need approval. Runs as line data
	// is loaded, before anyone can edit it. A rate of 0 is left for calculatePressProcessLineData to fill.
	stampAcceptedRates() {
		(this.pressProcesses || []).forEach((l) => {
			if (l.defaultHourlyRunningCostLabour === undefined && l.hourlyRunningCostLabour > 0) {
				l.defaultHourlyRunningCostLabour = l.hourlyRunningCostLabour;
			}
			if (l.defaultHourlyRunningCostMachine === undefined && l.hourlyRunningCostMachine > 0) {
				l.defaultHourlyRunningCostMachine = l.hourlyRunningCostMachine;
			}
		});
		(this.toolingCosts || []).forEach((t) => {
			t.defaultDesignRate = t.defaultDesignRate ?? t.designRate;
			t.defaultAutoFormRate = t.defaultAutoFormRate ?? t.autoFormRate;
			t.defaultMachineRate = t.defaultMachineRate ?? t.machineRate;
			t.defaultAssemblyRate = t.defaultAssemblyRate ?? t.assemblyRate;
			t.defaultQualityRate = t.defaultQualityRate ?? t.qualityRate;
		});
	}

	toggleChild(child: QuotationAnalysisForm) {
		this.expandedChildId = this.expandedChildId === child.id ? null : child.id;
		if (this.expandedChildId === child.id) { this.parseChildLineData(child); }
	}

	toggleFsSection(key: string) {
		this.fsSections[key] = !this.fsSections[key];
	}

	toggleTool(index: number) {
		this.toolingCosts[index].expanded = !this.toolingCosts[index].expanded;
	}

	updateChildTotals() {
		// A child used more than once in the assembly counts once per use. Piece price only - the child's
		// tooling is a one-off cost and is not multiplied.
		this.childTotals.totalPartCost = this.childQAFs.reduce((n, { totalPartCost, quantityPerAssembly }) => n + (totalPartCost || 0) * (quantityPerAssembly || 1), 0);
		this.childTotals.grandTotal = this.childQAFs.reduce((n, { grandTotal }) => n + (grandTotal || 0), 0);
		this.childTotals.batchPrice = this.childQAFs.reduce((n, { batchPrice }) => n + batchPrice, 0);
		this.childTotals.batchCost = this.childQAFs.reduce((n, { batchCost }) => n + batchCost, 0);
		this.childTotals.batchValueAdded = this.childQAFs.reduce((n, { batchValueAdded }) => n + batchValueAdded, 0);
		this.childTotals.annualPrice = this.childQAFs.reduce((n, { annualPrice }) => n + annualPrice, 0);
		this.childTotals.annualCost = this.childQAFs.reduce((n, { annualCost }) => n + annualCost, 0);
		this.childTotals.annualValueAdded = this.childQAFs.reduce((n, { annualValueAdded }) => n + annualValueAdded, 0);
		this.childTotals.lifetimePrice = this.childQAFs.reduce((n, { lifetimePrice }) => n + lifetimePrice, 0);
		this.childTotals.lifetimeCost = this.childQAFs.reduce((n, { lifetimeCost }) => n + lifetimeCost, 0);
		this.childTotals.lifetimeAddedValue = this.childQAFs.reduce((n, { lifetimeAddedValue }) => n + lifetimeAddedValue, 0);
		// Tooling and fixtures sum over the whole subtree: a child's own totalToolingCosts covers only
		// its own tools (calculateToolingCosts never rolls its children in), so summing the direct
		// children alone leaves every grandchild's tooling out of the assembly total.
		const toolingScope = this.getToolingScopeQAFs();
		this.childTotals.totalToolingCosts = toolingScope.reduce((n, { totalToolingCosts }) => n + (totalToolingCosts || 0), 0);
		this.childTotals.totalFixtureCosts = toolingScope.reduce((n, { totalFixtureCosts }) => n + (totalFixtureCosts || 0), 0);
		this.childTotals.qualityLevelCost = this.childQAFs.reduce((n, { qualityLevelCost }) => n + qualityLevelCost, 0);
		this.childTotals.engineeringCost = this.childQAFs.reduce((n, { engineeringCost }) => n + (engineeringCost || 0), 0);
		this.childTotals.adminCost = this.childQAFs.reduce((n, { adminCost }) => n + (adminCost || 0), 0);
		// Roll the child piece prices into the parent's own piece price now that the child totals changed.
		if (this.recalculatingChild === false) {
			this.calculateFinancialSummary();
		}
	}

	updateItem() {
		if (this.isSaving) return;
		this.isSaving = true;
		this.prepareItemForSaving();
		this.qafService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					this.isSaving = false;
					this.toastService.success("Successfully Updated");
				},
				(err) => {
					this.isSaving = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	// Router grid: reorder rows by drag-and-drop. Router order is the array order (no stage field on a
	// press-process line), so this just moves the row within the array and recomputes.
	updateRouterOrder(e: any) {
		const visibleRows = e.component.getVisibleRows();
		const toIndex = this.pressProcesses.indexOf(visibleRows[e.toIndex].data);
		const fromIndex = this.pressProcesses.indexOf(e.itemData);
		if (fromIndex < 0 || toIndex < 0) { return; }

		this.pressProcesses.splice(fromIndex, 1);
		this.pressProcesses.splice(toIndex, 0, e.itemData);
		this.pressProcesses = [...this.pressProcesses];
		this.calculatePressProcessLineData();
	}
	uploadCADFile(file: File) {
		const containerName = "cadfiles";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupCADFile.lookupId = res.response[0].lookupId;
					this.popupCADFile.fileName = res.response[0].name;
				},
				(err) => {
					console.log(err);
					this.toastService.error("CAD File Upload Failed - Please ensure you are uploading a valid file");
				}
			);
	}

	uploadImage(file: File) {
		const containerName = "images";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		uploadData.append("mimeTypeKey", "ImageMimeTypes");
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.item.partImageUrl = `${environment.server.storageUrl}/${containerName}/${res.response[0].location}`;
					this.setImageStyle(this.item.partImageUrl);
					this.toastService.success("Image uploaded - Click Save to confirm change");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Image Upload Failed - Please ensure you are uploading a valid image file");
				}
			);
	}

	uploadSectionDocument(file: File) {
		const uploadData = new FormData();
		uploadData.append("containerName", "supportingdocuments");
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupSectionDocument.lookupId = res.response[0].lookupId;
					this.popupSectionDocument.fileName = res.response[0].name;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Document upload failed - please check the file and try again");
				}
			);
	}

	uploadSupportingDocument(file: File) {
		const containerName = "supportingdocuments";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupSupportingDocument.lookupId = res.response[0].lookupId;
					this.popupSupportingDocument.fileName = res.response[0].name;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Supporting Document Upload Failed - Please ensure you are uploading a valid file");
				}
			);
	}

	viewChildQAF(e: any) {
		this.router.navigate([`${this.returnUrl}/${e.row.data.id}`]).then(() => window.location.reload());
	}
}
